import {
  Fragment,
  useEffect,
  useState,
  useRef,
  useMemo,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { axiosGet, axiosPost } from "../services/axiosService";
import { variableParser } from "../services/parserService";
import { BotComponent, UserComponent } from "./ChatComponent";
import env from "../services/environment";
import "../services/firebaseService";
import "../style/preview.css";
import { BotIconOld, WhiteBotIcon, OutgrowBotIcon } from "./Icons";
import { deleteCookies, setCookie, getCookie } from "../services/cookieService";
import { io } from "socket.io-client";
import useScrollStore from "@/services/useScrollStore";
import { nanoid } from "nanoid";
import { useCalendlyEventListener } from "react-calendly";
import getTextColor from "@/utils/getTextColor"; // cool
import { useRTLDirection } from "@/hooks/useRTLDirection";
import { produce } from "immer";
import _ from "lodash";
import { motion } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import "@/style/swipper.css";
import trackError from "@/utils/track-error";
import {
  ACCEPT_MESSAGE,
  COMPOSE_MESSAGE_PLACEHOLDER,
  DECLINE_MESSAGE,
  EMAIL_VALIDATION_MESSAGE,
  ERROR_529,
  ERROR_CONNECTION_ISSUE,
  QUESTION_LIMIT_REACHED_MESSAGE,
  RETRY_LABEL,
  // SOMETHING_WENT_WRONG_MESSAGE,
  TAKING_LONGER_THAN_EXPECTED_MESSAGE,
  WAIT_BEFORE_TRYING_MESSAGE,
} from "@/language.constants";
import { removeCustomTags, stripHTML } from "@/utils/renderUtility";
import ProtectedBranding from "./ProtectedBranding";
import { useReasoningManager } from "@/hooks/useReasoningManager";
import deriveShade from "@/utils/deriveShade";
import { useParentWindowWidth } from "@/hooks/useParentWindowWidth";
import getColorWithAlpha from "@/utils/getColorWithAlpha";

declare global {
  interface Window {
    loadGTM: () => void;
    loadGTMDirect: () => void;
    gtmLoaded: boolean;
    google_tag_manager: any;
    dataLayer: any[];
    cbbSettings: {
      base_url: string;
      app_id: string;
      app_type: string;
    };
    cbbConsentHandlers: {
      accept: () => void;
      decline: () => void;
      loadGTM: () => void;
    };
  }
}

const PREDEFINED_VARIABLES = [
  "location",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
];
const environmentData = env();
const socket = io(environmentData.SERVER_API_URL + "/live-calc", {
  transports: ["websocket"],
  autoConnect: false,
});

const Preview = forwardRef(function (props: any, ref) {
  const {
    embed,
    environment,
    consent,
    setConsent,
    setReadPdf,
    setReadPdfText,
    locationData = {},
    embedData,
    contactData = null,
    setContactData,
    roomId,
    setRoomId,
    showBackBtn,
    isPreviewModeChat,
    creditId,
    variableData,
    fromOutgrow,
    isTrial,
  } = props;
  const router = useNavigate();
  const [searchParams] = useSearchParams();
  const { parentWidth, isMobileParent } = useParentWindowWidth();
  const debug = useRef<boolean>(searchParams.has("debug"));
  const steps = useRef<boolean>(searchParams.has("steps"));
  const senderId = useMemo(() => {
    const temp = getCookie("senderId")
      ? getCookie("senderId")
      : String(Math.floor(Math.random() * 1000000));
    return temp;
  }, []);
  // const botData = props?.botData ?? {};tmp
  const [botData, setBotData] = useState<any>(props?.botData ?? {});
  const [userCredential, setUserCredential] = useState({
    id: senderId,
    email: "",
    phone: "",
    name: "",
    salesForce: "",
  });
  const [chatConfig, setChatConfig] = useState<any>(props?.botData?.chatConfig);
  const rtl = useRTLDirection(chatConfig);
  const [questions, setQuestions] = useState<any>([]);
  const [isReplyValid, setIsReplyValid] = useState(true);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState("");
  const [botCustomId, setBotCustomId] = useState("");
  const [botId, setBotId] = useState("");
  const [botName, setBotName] = useState<any>();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<any>(null);
  const [buttonVariables, setButtonVariables] = useState<any>([]);
  const [islast, setIsLast] = useState<boolean>(false);
  const [isAnswerSaved, setIsAnswerSaved] = useState<boolean>(false);
  const [showError, setShowError] = useState<boolean>(false);
  const [botType, setBotType] = useState<string>("");
  const [localContactData, setLocalContactData] = useState(contactData);
  const [hasUserSentMessage, setHasUserSentMessage] = useState<boolean>(false);
  const hasUserMessagedRef = useRef<boolean>(false); // Track if user has sent at least one message
  const [message, setMessage] = useState<any>([]);

  useEffect(() => {
    if (contactData) setLocalContactData(contactData);
  }, [contactData]);

  const [newReply, setNewReply] = useState("");
  const newReplyLast = useRef(newReply);
  const handleUserReplyRef = useRef<(...args: any[]) => Promise<void> | void>(
    () => {},
  );
  const lastSourceQuery = useRef("");
  const [responseLoader, setResponseLoader] = useState<boolean>(false);
  const prevResponseLoaderRef = useRef<boolean>(false);

  // Fire CREDIT_CONSUMED to parent whenever the response loader turns off
  useEffect(() => {
    const wasLoading = prevResponseLoaderRef.current;
    prevResponseLoaderRef.current = responseLoader;

    // true → false: loader just turned off, fire the event
    if (wasLoading && !responseLoader) {
      console.log("[CREDIT_CONSUMED] postMessage fired to parent");
      window.parent.postMessage({ type: "CREDIT_CONSUMED" }, "*");
    }
  }, [responseLoader]);

  // Track when to show credits: ONLY after user sends a message AND gets a bot response (and loader stops)
  useEffect(() => {
    const totalCredits = Number(
      localContactData?.fieldData?.totalCreditsPurchased || 0,
    );

    // Don't show credits if no credits are configured
    if (totalCredits <= 0) {
      return;
    }

    // Don't show credits while bot is loading/typing
    if (responseLoader) {
      return;
    }

    // Check if there's at least one user message (author === 1)
    const hasUserMsg = message.some((msg: any) => msg.author === 1);

    if (hasUserMsg) {
      hasUserMessagedRef.current = true;

      // Find the index of the first user message
      const firstUserMsgIndex = message.findIndex(
        (msg: any) => msg.author === 1,
      );

      // Check if there's a bot response (author === 0) AFTER the first user message
      const hasBotResponseAfterUserMsg = message
        .slice(firstUserMsgIndex + 1)
        .some((msg: any) => msg.author === 0);

      // Only show credits if bot has responded to user's message AND loader has stopped
      if (hasBotResponseAfterUserMsg) {
        setHasUserSentMessage(true);
      }
    }
  }, [
    message,
    localContactData?.fieldData?.totalCreditsPurchased,
    responseLoader,
  ]);
  const [textColor, setTextColor] = useState<any>("");
  const [starterQues, setStarterQues] = useState<any>();
  const [tempStore, setTempStore] = useState<any>();
  const [activeCat, setActiveCat] = useState<any>();
  const [arr, setArr] = useState<any>([]);
  const [arr2, setArr2] = useState<any>([]);
  const [arr3, setArr3] = useState<any>([]);
  const [checkMsg, setCheckMsg] = useState<any>(false);
  const [checkIndex, setCheckIndex] = useState<any>(false);
  const [checkValidate, setCheckValidate] = useState<any>(false);
  const person = 1;
  const ai = 0;
  const [disableInput, setDisableInput] = useState<any>();
  const nudgeTimer = useRef<any>();
  const nudgeTimer3 = useRef<any>();
  const responseTimer = useRef<any>();
  const nudgeTimer2 = useRef<any>();
  const finalMsg = useRef<any>();
  const isSkip = useRef<boolean>(true);
  const regenerate = useRef<any>();
  const scrollRef = useRef<HTMLSpanElement>(null);
  const scrollToButtomRef = useRef<HTMLDivElement>(null);
  const [suggestedQuesAns, setSuggestedQuesAns] = useState<boolean>(false);
  finalMsg.current = message[message.length - 1];
  const [startLiveChat, setStartLiveChat] = useState(false);
  const [liveChatNode, setLiveChatNode] = useState<any>();
  const [dataSource, setDataSource] = useState<any[]>([]);
  const dataSourceRef = useRef<any[]>([]);
  const handoffData = useRef<{
    isConnected: boolean;
    handoffType: string;
    configId: string;
    payload: Record<string, any>;
  }>({
    isConnected: false,
    handoffType: "",
    configId: "",
    payload: {},
  });
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const processedAgentMsg = useRef(new Set<string>());
  const [calendlyConfig, setCalendlyConfig] = useState<any>({});
  const roomID = useRef<any>();
  const chatHandlerRef = useRef<string>("ai");
  const socketRoomId = useRef(nanoid());
  const [scrapedData, setScrapedData] = useState<Record<string, string>>({});
  const queryRequestSignal = useRef<AbortController>();
  const [chatSummary, setChatSummary] = useState("");
  const [nextChatSummaryStart, setNextChatSummaryStart] = useState(0);
  const [chatSummaryPending, setChatSummaryPending] = useState(false);
  const [overLimit, setOverLimit] = useState(false);
  const [usingToolLoader, setUsingToolLoader] = useState(false);
  const [showGoToBottom, setShowGoToBottom] = useState(false);
  const [isAutoScrollPaused, setIsAutoScrollPaused] = useState(false);
  const initialLoadComplete = useRef(false);
  const hasUserScrolled = useRef(false);
  const isAtBottom = useRef(true);
  const bottomObserver = useRef<IntersectionObserver | null>(null);
  const queryRequestId = useRef("");
  const isStreamingRef = useRef(false); // Track if /query streaming is in progress
  const pendingToolData = useRef<{ toolCalls: any[]; toolResults: any[] }>({
    toolCalls: [],
    toolResults: [],
  });
  const botComponentRef = useRef<any>();
  const showCalendlyBtn = useRef(false);
  const [responseCacheOptions, setResponseCacheOptions] = useState<{
    id: string;
    preserved: boolean;
    answered?: boolean;
  } | null>(null);
  const creditConsumedNotifiedRef = useRef(false);
  const isMobile =
    typeof window !== "undefined" &&
    /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  const responsive = window.innerWidth <= 768;
  const [borderRadius, setBorderRadius] = useState(100);
  const formattedLocation = useMemo(() => {
    function capitalize(str: string): string {
      if (!str) return "";
      return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    }

    if (locationData?.country) {
      return `${capitalize(locationData.city)},${capitalize(locationData.region)},${capitalize(locationData.country)}`;
    }
    return "Not Detected";
  }, [locationData]);
  const customGoalsTriggered = useRef(false);
  const customGoalsMsg = useRef("");
  const errorTimeoutRef = useRef<any>(null);
  const pendingErrorMessage = useRef<string>("");

  const [cooldownActive, setCooldownActive] = useState(false);
  const [cooldownRemainingTime, setCooldownRemainingTime] = useState(0);
  const [cooldownMessage, setCooldownMessage] = useState("");
  const [isCustomProviderError, setIsCustomProviderError] = useState(false);
  const reasoning = useReasoningManager();

  const loadGTMNow = useCallback(() => {
    console.log("=== LOADING GTM ===");

    const GTM_ID = "GTM-PS96TMC8";

    // Check if already loaded
    if (
      window.gtmLoaded ||
      document.querySelector(`script[src*="${GTM_ID}"]`)
    ) {
      console.log("GTM already loaded");
      return;
    }

    // Initialize dataLayer FIRST
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      "gtm.start": new Date().getTime(),
      event: "gtm.js",
    });
    console.log("DataLayer initialized:", window.dataLayer);

    // Create and load GTM script
    const gtmScript = document.createElement("script");
    gtmScript.async = true;
    gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;

    // Add load event listeners
    gtmScript.onload = () => {
      console.log("GTM script loaded successfully");
      window.gtmLoaded = true;

      // Verify script is in DOM
      const scriptInDom = document.querySelector(`script[src*="${GTM_ID}"]`);
      console.log("Script in DOM:", !!scriptInDom);

      // Check if GTM object exists
      setTimeout(() => {
        console.log(
          "Google Tag Manager object:",
          typeof window.google_tag_manager,
        );
        console.log("DataLayer after load:", window.dataLayer);
      }, 1000);
    };

    gtmScript.onerror = (error) => {
      console.error("Failed to load GTM script:", error);
    };

    // Add to head
    document.head.appendChild(gtmScript);
    console.log("GTM script element created and added to head");

    // Verify it was added
    setTimeout(() => {
      const addedScript: any = document.querySelector(
        `script[src*="${GTM_ID}"]`,
      );
      console.log("Script added to DOM:", !!addedScript);
      if (addedScript) {
        console.log("Script src:", addedScript.src);
      }
    }, 100);

    // Add noscript fallback
    const noscript = document.createElement("noscript");
    const iframe = document.createElement("iframe");
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${GTM_ID}`;
    iframe.height = "0";
    iframe.width = "0";
    iframe.style.display = "none";
    iframe.style.visibility = "hidden";
    noscript.appendChild(iframe);
    document.body.appendChild(noscript);

    console.log("GTM noscript fallback added");
  }, []);

  const useGTMLoader = (botData: any) => {
    const gtmLoadedRef = useRef(false);

    const checkPrivacyAndLoadGTM = useCallback(() => {
      try {
        const localStorageValue =
          window.localStorage?.getItem("privacy_policy");
        const cookieValue = document.cookie
          .split("; ")
          .find((row) => row.startsWith("privacy_policy="))
          ?.split("=")[1];

        if (localStorageValue === "true" || cookieValue === "true") {
          // GTM loading is now handled by the embed script
        }
      } catch (error) {
        console.error("Error checking privacy policy:", error);
      }
    }, []);

    return { checkPrivacyAndLoadGTM };
  };

  const lastUserMsgIndex = useMemo(() => {
    return message?.reduce((acc, val, idx) => {
      if (val?.author === 1) {
        acc = idx;
      }

      return acc;
    }, message?.length - 1);
  }, [message]);

  useImperativeHandle(ref, () => {
    return {
      sendAssistantMessage: (message) => {
        setMessage((prevItems) => [
          ...prevItems,
          {
            author: ai,
            timestamp: new Date(),
            text: message,
            aiResponse: true,
          },
        ]);
      },
      sendUserMessage: (message) => handleUserReply(0, false, message, false),
    };
  }, [botCustomId, roomId, botData]);

  const { checkPrivacyAndLoadGTM } = useGTMLoader(botData);

  useEffect(() => {
    checkPrivacyAndLoadGTM();
  }, [checkPrivacyAndLoadGTM]);

  // useEffect(() => {
  //   if (scrollToButtomRef.current && message.length === 0) {
  //     const timer = setTimeout(() => {
  //       if (scrollToButtomRef.current) {
  //         scrollToButtomRef.current.scrollTop = 0;
  //       }
  //     }, 100);
  //     return () => clearTimeout(timer);
  //   }
  // }, [message.length]);

  const userData = useMemo(() => {
    let data: Record<string, any> = {
      location: locationData.location ?? "",
    };

    data = mergeData(data, contactData?.fieldData);
    data = mergeData(data, embedData);
    data = mergeData(data, scrapedData);
    // data = mergeData(data, locationData);
    return data;
  }, [locationData, embedData, contactData, scrapedData]);

  const [isAiResError, setIsAiResError] = useState(false);
  const [editable, setEditable] = useState(false);
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === "BOT_DATA_UPDATE") {
        setBotData(event.data.botData);
        setChatConfig(event.data.botData?.chatConfig);

        if (
          event.data.botData?.aiStarterQues?.initialMsg &&
          event.data.botData?.aiStarterQues?.data.length
        ) {
          setTempStore(event.data.botData.aiStarterQues?.data);
          setCheckMsg(!!event.data.botData.aiStarterQues?.initialMsg);
          setStarterQues(event.data.botData.aiStarterQues?.data);
        }
      }
    };
    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  useEffect(() => {
    handleUserReplyRef.current = handleUserReply;
  }, [handleUserReply]);

  useEffect(() => {
    window.parent.postMessage({ type: "livebot:preview:ready" }, "*");

    const handlePreviewMessage = (event: MessageEvent) => {
      if (!event.data || typeof event.data !== "object") return;
      const data = event.data as {
        type?: string;
        sequence?: number;
        payload?: { text?: string };
      };
      if (data.type !== "livebot:preview:user-reply") return;
      const messageText =
        typeof data.payload?.text === "string" ? data.payload.text : "";

      Promise.resolve()
        .then(() => {
          if (messageText.trim().length === 0) return;
          return handleUserReplyRef.current(0, false, messageText, false);
        })
        .finally(() => {
          window.parent.postMessage(
            { type: "livebot:preview:reply-handled", sequence: data.sequence },
            "*",
          );
        });
    };

    window.addEventListener("message", handlePreviewMessage);
    return () => {
      window.removeEventListener("message", handlePreviewMessage);
    };
  }, []);

  function handleCooldownStarted(data) {
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = null;
    }
    pendingErrorMessage.current = "";

    setCooldownActive(true);
    setCooldownMessage(
      data.message ||
        WAIT_BEFORE_TRYING_MESSAGE[
          botData?.aiBotConfig?.language?.toLowerCase()
        ] ||
        WAIT_BEFORE_TRYING_MESSAGE.english,
    );
    setCooldownRemainingTime(data.cooldownPeriod || 60);
    setIsCustomProviderError(data.isCustomProvider || false);
    setResponseLoader(false);
    setIsAiResError(false);
  }

  function handleCooldownStatus(data) {
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = null;
    }
    pendingErrorMessage.current = "";

    setCooldownRemainingTime(data.remainingTime || 0);
    setIsCustomProviderError(data.isCustomProvider || true);
    if (data.message) {
      setCooldownMessage(data.message);
    }
  }
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    if (!isMobile && !embed) {
      const scrollY = window.scrollY;
      const originalBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      Object.assign(document.body.style, {
        position: "fixed",
        top: `-${scrollY}px`,
        width: "100%",
      });

      return () => {
        document.body.style.cssText = "";
        document.body.style.overflow = originalBodyOverflow;
        window.scrollTo(0, scrollY);
      };
    }
  }, [isMobile, embed]);

  useEffect(() => {
    const textarea = textareaRef.current;
    const handleFocus = () => {
      window.parent.postMessage({ type: "BOT_INPUT_FOCUSED" }, "*");
    };
    const handleBlur = () => {
      window.parent.postMessage({ type: "BOT_INPUT_BLUR" }, "*");
    };

    if (textarea) {
      textarea.addEventListener("focus", handleFocus);
      textarea.addEventListener("blur", handleBlur);
    }

    return () => {
      if (textarea) {
        textarea.removeEventListener("focus", handleFocus);
        textarea.removeEventListener("blur", handleBlur);
      }
    };
  }, []);

  useCalendlyEventListener({
    onEventScheduled: (e) => {
      setMessage((prevItems) => [
        ...prevItems,
        {
          author: ai,
          timestamp: new Date(),
          text: "Meeting scheduled successfully.",
        },
      ]);
    },
  });

  useEffect(() => {
    const handleScroll = () => {
      // Don't blur on iOS/mobile as it interferes with keyboard behavior
      if (isMobile) return;

      const active = document.activeElement;
      if (
        active instanceof HTMLInputElement ||
        active instanceof HTMLTextAreaElement
      ) {
        active.blur();
      }
    };

    const wrapperElement = document.querySelector(
      ".cbb-full-screen-preview-wrapper, .cbb-embed-preview-wrapper",
    ) as HTMLElement;

    if (wrapperElement) {
      wrapperElement.addEventListener("scroll", handleScroll, {
        passive: true,
      });
      return () => wrapperElement.removeEventListener("scroll", handleScroll);
    } else {
      window.addEventListener("scroll", handleScroll);
      return () => window.removeEventListener("scroll", handleScroll);
    }
  }, [isMobile]);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const handleWheel = (event: WheelEvent) => {
      const target = event.target as HTMLElement;
      const scrollable = target.closest(
        ".previewContainer, .new-scrollbar",
      ) as HTMLElement;

      if (scrollable) {
        const { scrollTop, scrollHeight, clientHeight } = scrollable;
        const atTop = scrollTop === 0;
        const atBottom = scrollTop + clientHeight >= scrollHeight - 1;

        if ((event.deltaY < 0 && atTop) || (event.deltaY > 0 && atBottom)) {
          event.preventDefault();
        }
      }
      event.stopPropagation();
    };

    // Add non-passive wheel event listener to allow preventDefault
    wrapper.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      wrapper.removeEventListener("wheel", handleWheel);
    };
  }, []);

  const mergedSalesForcePayload = questions.reduce(
    (acc: Record<string, any>, question) => {
      if (question.name === "salesForce" && question.salesForceData?.payload) {
        return { ...acc, ...question.salesForceData.payload };
      }
      return acc;
    },
    {},
  );
  const firstMessage = useRef<any[]>([]);

  const [suggestedQuestionsData, setSuggestedQuestionsData] = useState<any[]>(
    [],
  );

  // useEffect(() => {
  //   if (
  //     !lastMessage ||
  //     !lastMessage?.text ||
  //     !botData?.aiBotConfig?.showFollowUps
  //   )
  //     return;
  //   const content = String(lastMessage.text ?? "");
  //   const tag = "clickable-options-cbb";

  //   setSuggestedQuestionsData(
  //     extractClickableOptionsData(content, tag, {}) ?? [],
  //   );
  // }, [
  //   lastMessage?.text,
  //   extractClickableOptionsData,
  //   botData?.aiBotConfig?.showFollowUps,
  // ]);

  useEffect(() => {
    if (!initialLoadComplete.current) return;

    if (responseLoader && scrollToButtomRef.current) {
      const scrollContainer = scrollToButtomRef.current;
      requestAnimationFrame(() => {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      });
    }
  }, [responseLoader]);

  const handleStop = async () => {
    queryRequestSignal.current?.abort();
    if (!!queryRequestId?.current) {
      axiosGet(`/ai/stop-query?requestId=${queryRequestId.current}`).catch(
        console.error,
      );
    }
    setResponseLoader(false);
    setUsingToolLoader(false);
    botComponentRef.current?.setLoadingSeperation(false);
    // Clear any pending save operations when stopping
    // pendingSaveAnswer.current = null;
  };

  useEffect(() => {
    handleResponseTimer();
  }, [responseLoader, message, handleStop, usingToolLoader, reasoning]);

  function handleResponseTimer() {
    if (responseTimer?.current) clearTimeout(responseTimer.current);
    if (reasoning.ongoing) return;

    if (responseLoader) {
      let timeout = 45000;
      if (botData?.aiBotConfig?.configId) {
        timeout = botData?.configAiModel?.includes("grok")
          ? 90000
          : (timeout = botData?.configAiModel?.includes("Gemini")
              ? 60000
              : 35000);
      } else if (botData?.botModel?.includes("auto")) {
        timeout = 60000;
      } else if (botData?.botModel?.includes("grok")) {
        timeout = 90000;
      } else if (botData?.botModel?.includes("Gemini")) {
        timeout = 60000;
      }
      if (responseTimer.current) {
        clearTimeout(responseTimer.current);
      }

      responseTimer.current = setTimeout(() => {
        if (usingToolLoader) {
          setResponseLoader(false);
          const msgToShow =
            TAKING_LONGER_THAN_EXPECTED_MESSAGE[
              botData?.aiBotConfig?.language?.toLowerCase()
            ] || TAKING_LONGER_THAN_EXPECTED_MESSAGE.english;

          setMessage((prevMessages) => {
            const tempMessages = structuredClone(prevMessages);
            const lastMessage = tempMessages[tempMessages.length - 1];
            if (lastMessage?.isLoader) {
              lastMessage.text = msgToShow;
              lastMessage.isLoader = false;
              lastMessage.isTemp = true;
            } else {
              tempMessages.push({
                text: msgToShow,
                author: ai,
                isTemp: true,
              });
            }
            return tempMessages;
          });
          return;
        }

        if (debug.current) return;
        setResponseLoader(false);

        setMessage((prevMessages) => {
          const lastMessage = prevMessages[prevMessages.length - 1];

          if (lastMessage?.isLoader) {
            queryRequestSignal.current?.abort();
            setIsAiResError(true);
            setResponseLoader(false);

            console.log("STREAMING ABORTED AS LAST MESSAGE IS LOADER");
            return prevMessages.slice(0, -1);
          } else {
            console.log("STREAMING NOT ABORTED AS LAST MESSAGE IS NOT LOADER");
            return prevMessages;
          }
        });

        trackError(
          new Error(
            `live-calc: response timeout after ${timeout / 1000} sec, query - ${newReplyLast.current}`,
          ),
        );
      }, timeout);
    }
  }

  useEffect(() => {
    if (
      !env()?.isAI &&
      ((botType == "ai" && !botData?.isTrained) ||
        ["button", "sendMessage"].includes(
          questions[currentQuestionIndex ?? 0]?.name,
        ))
    )
      setDisableInput(true);
    else setDisableInput(false);
  }, [currentQuestionIndex]);

  // useEffect(() => {
  //   if (chatConfig?.typography?.font?.fontURL) {
  //     let font;
  //     const id = "dynamic-font";
  //     if (document.getElementById(id)) {
  //       font = document.getElementById(id);
  //       font.href = chatConfig?.typography?.font?.fontURL;
  //     } else {
  //       font = document.createElement("link");
  //       font.rel = "stylesheet";
  //       font.id = id;
  //       font.href = chatConfig?.typography?.font?.fontURL;
  //       document.head.appendChild(font);
  //     }
  //   }
  // }, [chatConfig?.typography]);

  useEffect(() => {
    const selectedColor = chatConfig?.color;
    document.documentElement.style.setProperty(
      "--dynamic-color",
      chatConfig?.color,
    );

    const color = getTextColor(selectedColor);
    setTextColor(color);
  }, [chatConfig?.color]);

  useEffect(() => {
    if (message.length > 0 && !initialLoadComplete.current) {
      const timer = setTimeout(() => {
        initialLoadComplete.current = true;
        const scrollContainer = scrollToButtomRef.current;
        if (scrollContainer) {
          const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
          const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
          if (distanceFromBottom > 100) {
            setShowGoToBottom(true);
            setIsAutoScrollPaused(true);
          }
        }
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [message.length]);
  useEffect(() => {
    const scrollContainer = scrollToButtomRef.current;
    const bottomAnchor = scrollRef.current;

    if (!scrollContainer || !bottomAnchor) return;
    if (bottomObserver.current) {
      bottomObserver.current.disconnect();
    }
    bottomObserver.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        const isVisible = entry.isIntersecting;

        isAtBottom.current = isVisible;

        // Only show button if:
        // 1. Initial load is complete (prevents flicker)
        // 2. User has scrolled at least once (prevents showing on first render)
        // 3. Bottom anchor is NOT visible (user is away from bottom)
        if (
          initialLoadComplete.current &&
          hasUserScrolled.current &&
          !isVisible
        ) {
          setShowGoToBottom(true);
          setIsAutoScrollPaused(true);
        } else if (isVisible) {
          setShowGoToBottom(false);
          setIsAutoScrollPaused(false);
        }
      },
      {
        root: scrollContainer,
        rootMargin: "0px 0px 50px 0px",
        threshold: 0,
      },
    );

    bottomObserver.current.observe(bottomAnchor);

    return () => {
      if (bottomObserver.current) {
        bottomObserver.current.disconnect();
      }
    };
  }, [message.length]);

  useEffect(() => {
    if (!initialLoadComplete.current || isAutoScrollPaused) {
      return;
    }

    if (message.length > 0 && scrollToButtomRef.current && isAtBottom.current) {
      const scrollContainer = scrollToButtomRef.current;
      requestAnimationFrame(() => {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      });
    }
  }, [message.length, isAutoScrollPaused]);

  // useEffect(() => {
  //   if (!embed) return;
  //   if (message.length === 0) return;
  //   const lastMessage = message[message.length - 1];

  //   if (
  //     lastMessage.author === ai &&
  //     !lastMessage.isLoader &&
  //     lastMessage.text &&
  //     lastMessage.aiResponse
  //   ) {
  //     const notificationTimer = setTimeout(() => {
  //       // Send notification to parent window (embed script)
  //       window.parent.postMessage(
  //         {
  //           type: 'NEW_BOT_MESSAGE',
  //           timestamp: Date.now(),
  //           messagePreview: typeof lastMessage.text === 'string'
  //             ? lastMessage.text.substring(0, 50)
  //             : '',
  //           hasAgent: !!lastMessage.agent
  //         },
  //         '*'
  //       );
  //     }, 100);

  //     return () => clearTimeout(notificationTimer);
  //   }
  // }, [message, embed, ai]);

  // useEffect(() => {
  //   if (!embed) return;
  //   if (message.length === 0) return;
  //   const lastMessage = message[message.length - 1];

  //   // Notify for human agent messages
  //   if (lastMessage?.agent && lastMessage.author !== ai) {
  //     const notificationTimer = setTimeout(() => {
  //       window.parent.postMessage(
  //         {
  //           type: 'NEW_AGENT_MESSAGE',
  //           timestamp: Date.now(),
  //           agent: lastMessage.agent,
  //           messagePreview: typeof lastMessage.text === 'string'
  //             ? lastMessage.text.substring(0, 50)
  //             : ''
  //         },
  //         '*'
  //       );
  //     }, 100);

  //     return () => clearTimeout(notificationTimer);
  //   }
  // }, [message, embed, ai]);
  const sendUserQuery = useCallback(
    _.debounce(async (query: string) => {
      if (!query || !query.trim()) {
        setSuggestedQuestionsData([]);
        return;
      }
      if (
        query.trim().length <= 3 ||
        !socket ||
        !socket.connected ||
        responseLoader
      )
        return;
      socket.emit("user-query", { botCustomId, query: query.trim() });
    }, 500),
    [botCustomId, socket, responseLoader],
  );

  function handleSendViaEnter(e) {
    if (e.key === "Enter") {
      if (e.shiftKey) return;
      e.preventDefault();
      if (!responseLoader) handleSendReplyButton();
    }
  }

  // Track user scroll interaction (throttled for performance)
  const handleScroll = useCallback(() => {
    // Mark that user has scrolled at least once
    if (!hasUserScrolled.current) {
      hasUserScrolled.current = true;
    }
  }, []);

  // Scroll to bottom when the button is clicked
  const scrollToBottom = useCallback((event?: any) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (scrollToButtomRef.current) {
      const scrollContainer = scrollToButtomRef.current;
      const start = scrollContainer.scrollTop;
      const distance =
        scrollContainer.scrollHeight - scrollContainer.clientHeight - start;
      const duration = 300;
      const startTime = performance.now();

      const animateScroll = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easeProgress = 0.5 - Math.cos(progress * Math.PI) / 2;
        scrollContainer.scrollTop = start + distance * easeProgress;

        if (progress < 1) {
          requestAnimationFrame(animateScroll);
        } else {
          // After animation completes, ensure button is hidden
          setShowGoToBottom(false);
          setIsAutoScrollPaused(false);
        }
      };

      requestAnimationFrame(animateScroll);
    }
  }, []);

  // Attach scroll event listener with passive option for performance
  useEffect(() => {
    const container = scrollToButtomRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll, { passive: true });
    }

    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
    };
  }, [handleScroll]);

  useEffect(() => {
    if (islast) {
      if (startLiveChat) {
        setIsLast(false);
      } else {
        socket && socket.disconnect();
        deleteCookies("senderId");
        if (botData?.chatTranscript)
          axiosGet(`/livebot/sendChatTranscript?roomId=${roomId}`);
      }
    }
  }, [islast]);

  useEffect(() => {
    processBotData();

    if (props.resumeConversationData) {
      processResumeChat(props.resumeConversationData);
    }
  }, []);

  async function processBotData() {
    try {
      const {
        _id,
        botCustomId,
        questions,
        projectName,
        startNodeId,
        botType,
        aiBotConfig,
        companyId,
      } = botData;

      const currentIndex = questions.findIndex(
        (ele) => ele.drawFlowId === startNodeId,
      );

      if (
        botData.aiStarterQues?.initialMsg &&
        botData.aiStarterQues?.data.length
      ) {
        setTempStore(botData.aiStarterQues?.data);
        setCheckMsg(!!botData.aiStarterQues?.initialMsg);
      }
      if (!chatConfig) {
        setChatConfig(botData.chatConfig);
      }
      setBotId(_id);
      setBotCustomId(botCustomId);
      setQuestions(questions);
      setBotName(projectName);
      setCurrentQuestionIndex(props.resumeConversationData ? 1 : currentIndex);
      setBotType(botType);

      // await triggerNudge(botData.aiBotConfig?.nudgeConfig);
    } catch (err) {
      if (environment === "live") router("/error");
    }
  }

  async function processResumeChat(resumeConversationData) {
    const messages: any[] = [];

    for (const data of resumeConversationData.userAnswers) {
      if (data.userAnswer === "NO_REPLY") {
        messages.push({
          author: ai,
          timestamp: new Date(data.date),
          text: data.nodeTitle,
          ...(data.terminateByUser && {
            terminateByUser: data.terminateByUser,
          }),
        });
      } else {
        if (data.userAnswer) {
          messages.push({
            author: person,
            timestamp: new Date(data.date),
            text: data.userAnswer,
            ...(data.terminateByUser && {
              terminateByUser: data.terminateByUser,
            }),
          });
        }

        if (data.nodeTitle) {
          messages.push({
            author: ai,
            timestamp: new Date(data.date),
            text: data.nodeTitle,
            aiResponse: true,
            agent: data.agent,
            ...(data.terminateByUser && {
              terminateByUser: data.terminateByUser,
            }),
          });
        }
      }
    }

    if (resumeConversationData.handoffData) {
      handoffData.current = resumeConversationData.handoffData;

      if (
        handoffData.current.handoffType &&
        handoffData.current.payload?.conversationId
      ) {
        connectSocket(resumeConversationData._id);
        socket.emit("join-human-agent", {
          roomId: `${handoffData.current.handoffType}_${handoffData.current.payload.conversationId}`,
        });
      }
    }

    const chatHandler = handoffData.current.isConnected
      ? handoffData.current.handoffType
      : "ai";

    setRoomId(resumeConversationData._id);
    setMessage(messages);
    chatHandlerRef.current = chatHandler;
  }

  useEffect(() => {
    if (currentQuestionIndex !== null && currentQuestionIndex >= 0) {
      if (questions[currentQuestionIndex]?.name === "sendMessage") {
        const titleData = variableParser(
          questions[currentQuestionIndex]?.data?.title,
          variableData,
          null,
          mergedSalesForcePayload,
        );
        setMessage((prevItems) => [
          ...prevItems,
          {
            author: ai,
            timestamp: new Date(),
            text: titleData,
            name: questions[currentQuestionIndex]?.name,
            hasAnswered: false,
            completeData: questions[currentQuestionIndex],
          },
        ]);
        setDisableInput(true);
        handleSendNoReplyMessage(titleData);
      } else {
        if (!handoffData.current.isConnected) {
          chatHandlerRef.current = "ai";
        }
      }
    }
  }, [currentQuestionIndex]);

  const handleSendNoReplyMessage = async (title) => {
    try {
      const variableDataToSend = {};
      for (const variableName in variableData) {
        if (PREDEFINED_VARIABLES.includes(variableName))
          variableDataToSend[variableName] = variableData[variableName];
      }

      if (
        environmentData?.isAI &&
        questions[currentQuestionIndex].drawFlowId == 1
      ) {
        firstMessage.current = [
          {
            botCustomId: botCustomId,
            nodeTitle: typeof title === "string" ? title : (title?.value ?? ""),
            drawFlowId: questions[currentQuestionIndex].drawFlowId ?? 0,
            userAnswer: "NO_REPLY",
            variableData:
              variableDataToSend && Object.keys(variableDataToSend).length > 0
                ? variableDataToSend
                : {
                    location: `${locationData.city}, ${locationData.regionName}, ${locationData.country}`,
                  },
            userCredential,
            nodeType: questions[currentQuestionIndex].nodeType,
          },
        ];
      }

      if (
        !(
          environmentData?.isAI &&
          questions[currentQuestionIndex].drawFlowId == 1
        )
      ) {
        const response = await axiosPost(
          `/liveBot/saveAnswer?botId=${botId}&roomId=${roomId}&country=${
            formattedLocation
          }`,
          {
            botCustomId: botCustomId,
            nodeTitle: typeof title === "string" ? title : (title?.value ?? ""),
            drawFlowId: questions[currentQuestionIndex].drawFlowId ?? 0,
            userAnswer: "NO_REPLY",
            variableData:
              variableDataToSend && Object.keys(variableDataToSend).length > 0
                ? variableDataToSend
                : {
                    location: `${locationData.city}, ${locationData.regionName}, ${locationData.country}`,
                  },
            userCredential,
            nodeType: questions[currentQuestionIndex].nodeType,
            model: botData?.botModel,
          },
        );
        setRoomId(response.data.roomId);
      }

      let pendingMsg = { ...arr3[0] };
      if (
        arr2.length === 0 &&
        checkMsg &&
        ((pendingMsg.hasOwnProperty("text") &&
          message.length < pendingMsg?.text?.durationVal) ||
          !pendingMsg.hasOwnProperty("text"))
      ) {
        setStarterQues(tempStore);
        if (botData?.aiStarterQues?.initialMsg?.trim().length > 0) {
          setMessage((prevItems) => {
            // Prevent pushing a duplicate initial message if the flow already pushed an identical one
            if (
              prevItems.length > 0 &&
              prevItems[prevItems.length - 1].text ===
                botData?.aiStarterQues?.initialMsg
            ) {
              return prevItems;
            }
            return [
              ...prevItems,
              {
                author: ai,
                timestamp: new Date(),
                text: botData?.aiStarterQues?.initialMsg,
              },
            ];
          });
        }
        isSkip.current = true;
        setCheckMsg(false);
        setDisableInput(false);
        return;
      } else if (
        pendingMsg.hasOwnProperty("text") &&
        message.length >= pendingMsg?.text?.durationVal &&
        arr2.length === 0
      ) {
        isSkip.current = true;
        setArr3((prev: any) => {
          const [_, ...pendings] = prev;
          return pendings;
        });
        setMessage((prevItems) => [...prevItems, pendingMsg]);
      } else if (
        questions[currentQuestionIndex]?.outputs[0].connections !== undefined
      ) {
        let currentIndex = questions.findIndex(
          (x) =>
            x.drawFlowId ===
            questions[currentQuestionIndex]?.outputs[0].connections.node,
        );
        setCurrentQuestionIndex(currentIndex);
      } else {
        if (chatHandlerRef.current === "ai") {
          setCurrentQuestionIndex(questions.length - 1);
          setDisableInput(false);
          setCheckIndex(false);
          return;
        }
        setIsLast(true);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleButtonReply = (
    currentIndex,
    buttonSelected,
    buttonSelectedIndex,
    buttonValue,
    buttonImage = "",
  ) => {
    let oldMessages = [...message];
    let indexToUpdate = message.length - 1;
    oldMessages[indexToUpdate].hasAnswered = true;
    setMessage(oldMessages);

    setMessage((prevItems) => [
      ...prevItems,
      {
        author: person,
        timestamp: new Date(),
        text: buttonSelected,
        value: buttonValue,
        image: buttonImage,
      },
    ]);
    isSkip.current = true;
    let varName =
      message[currentIndex].completeData.variableName !== undefined
        ? message[currentIndex].completeData.variableName
        : null;
    let insideVarName =
      message[currentIndex].completeData.buttonDetails.variableName;
    const valuefButton =
      message[currentIndex].completeData.buttonDetails.buttons[
        buttonSelectedIndex
      ].value;

    let oldButtonVariables = [...buttonVariables];
    let newObj = {
      name: message[currentIndex].completeData.buttonDetails.variableName,
      value: valuefButton,
    };
    oldButtonVariables.push(newObj);
    setButtonVariables(oldButtonVariables);

    let variableDataToSend = {};
    if (varName !== null) {
      variableDataToSend = {
        [varName]: buttonSelected,
        [insideVarName]: valuefButton,
      };
    }

    axiosPost(
      `/liveBot/saveAnswer?botId=${botId}&roomId=${roomId}&country=${formattedLocation}`,
      {
        botCustomId: botCustomId,
        nodeTitle: message[currentIndex].completeData.data.title,
        drawFlowId: message[currentIndex].completeData.drawFlowId ?? 0,
        userAnswer: buttonSelected,
        variableName: varName,
        variableData: variableDataToSend,
        userCredential,
        model: botData?.botModel,
      },
    )
      .then((response) => {
        if (roomId === null) {
          setRoomId(response.data.roomId);
        }
        if (
          message[currentIndex].completeData?.outputs[0]?.connectionId ==
            "output_0" &&
          message[currentIndex].completeData?.outputs[0]?.connections?.node
        ) {
          let currentIndexValue = questions.findIndex(
            (x) =>
              x.drawFlowId ===
              message[currentIndex].completeData?.outputs[0]?.connections.node,
          );
          setCurrentQuestionIndex(currentIndexValue);
        } else if (
          message[currentIndex].completeData?.outputs[buttonSelectedIndex]
            ?.connections !== undefined
        ) {
          let currentIndexValue = questions.findIndex(
            (x) =>
              x.drawFlowId ===
              message[currentIndex].completeData?.outputs[buttonSelectedIndex]
                ?.connections.node,
          );
          setCurrentQuestionIndex(currentIndexValue);
        } else {
          setIsLast(true);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const getSimpleObject = (data, mainKey = "") => {
    let obj = {};
    const objKeys = Object.keys(data ?? {});
    objKeys.forEach((key) => {
      if (typeof data[key] !== "object") {
        if (mainKey.length > 0)
          obj = { ...obj, [`${mainKey}.${key}`]: data[key] };
        else obj = { ...obj, [`${key}`]: data[key] };
      } else if (Array.isArray(data[key])) {
        const arr = data[key];
        const arrLen = data[key].length;

        let temoObj = convertArrayToObj(arr, key);
        temoObj = getSimpleObject(temoObj, mainKey);
        obj = { ...obj, ...temoObj };
      } else {
        let tempObj = {};
        if (mainKey.length > 0)
          tempObj = getSimpleObject(data[key], `${mainKey}.${key}`);
        else tempObj = getSimpleObject(data[key], `${key}`);
        obj = { ...obj, ...tempObj };
      }
    });
    return obj;
  };

  const convertArrayToObj = (arr, key) => {
    let obj = {};
    arr.forEach((a, index) => {
      obj[`${key}.${index}`] = a;
    });
    return obj;
  };

  // const handleStarterQues = async (reply: string) => {
  //   try {
  //     if (!islast) {
  //       if (roomId === null && firstMessage.current.length !== 0) {
  //         function capitalize(str) {
  //           return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  //         }
  //         const location = `${capitalize(locationData.city)}, ${capitalize(locationData.region)}, ${capitalize(locationData.country)}`;
  //         const response = await axiosPost(
  //           `/liveBot/saveAnswer?botId=${botId}&roomId=${roomId}&country=${location}`,
  //           {
  //             botCustomId: botCustomId,
  //             nodeTitle: firstMessage.current[0].nodeTitle,
  //             drawFlowId: "1",
  //             userAnswer: "NO_REPLY",
  //             variableData: variableData,
  //             userCredential: userCredential,
  //             nodeType: firstMessage.current[0].nodeType,
  //             contactId: contactData?._id,
  //             userData,
  //             chatSummary: {
  //               summary: chatSummary,
  //               nextSummaryStart: nextChatSummaryStart,
  //               pending: chatSummaryPending,
  //             },
  //           },
  //         );

  //         roomID.current = response.data.roomId;
  //         setRoomId(response.data.roomId);
  //         connectSocket(response.data.roomId);

  //         setContactData(response.data?.contact);
  //         window.localStorage.setItem(
  //           "cbb-contact-id",
  //           response.data?.contact._id,
  //         );

  //         firstMessage.current = [];
  //       }

  //       await triggerNudge(botData?.aiBotConfig?.nudgeConfig);

  //       const isStaterQues = true;

  //       if (!activeCat && botData?.aiStarterQues.isCat) {
  //         let catQuesAns = botData?.aiStarterQues.data.filter(
  //           (item) => item.cat === reply,
  //         );
  //         setStarterQues(catQuesAns);
  //         setActiveCat({ text: reply });
  //       } else {
  //         setStarterQues(undefined);
  //         setActiveCat(undefined);
  //         setTempStore("");
  //         if (suggestedQuesAns) setSuggestedQuesAns(false);
  //         let ans = botData?.aiStarterQues?.data?.filter(
  //           (item) => item.ques == reply,
  //         );
  //         setMessage((prevItems: any) => {
  //           const tempMessage = [...prevItems];
  //           tempMessage.push({
  //             author: person,
  //             timestamp: new Date(),
  //             text: reply,
  //           });
  //           return tempMessage;
  //         });
  //         await axiosPost(
  //           `/liveBot/saveAnswer?botId=${botId}&roomId=${roomId ?? roomID.current}&country=${variableData?.location}&starterQues=${isStaterQues}`,
  //           {
  //             isAiBot: true,
  //             botCustomId: botCustomId,
  //             nodeTitle: environmentData.isAI
  //               ? message[message.length - 1]?.name === "ask_question" ||
  //                 message[message.length - 1]?.name === "email" ||
  //                 message[message.length - 1]?.name === "job" ||
  //                 message[message.length - 1]?.name === "name" ||
  //                 message[message.length - 1]?.name === "address" ||
  //                 message[message.length - 1]?.name === "company" ||
  //                 message[message.length - 1]?.name === "phone"
  //                 ? message[message.length - 1].text.value
  //                 : message[message.length - 1].text
  //               : (message[message.length - 1].text ?? ""),
  //             drawFlowId: message[currentQuestionIndex]?.drawFlowId ?? 0,
  //             userAnswer: reply,
  //             variableName: null,
  //             variableData: variableData,
  //             userCredential,
  //           },
  //         );
  //         setMessage((prevItems: any) => {
  //           const tempMessage = [...prevItems];
  //           tempMessage.push({
  //             author: ai,
  //             timestamp: new Date(),
  //             text: ans[0]?.ans,
  //           });
  //           return tempMessage;
  //         });
  //         await axiosPost(
  //           `/liveBot/saveAnswer?botId=${botId}&roomId=${roomId ?? roomID.current}&country=${variableData?.location}`,
  //           {
  //             //maybe here
  //             isAiBot: true,
  //             botCustomId: botCustomId,
  //             nodeTitle: ans[0]?.ans,
  //             drawFlowId: message[currentQuestionIndex]?.drawFlowId ?? 0,
  //             userAnswer: "NO_REPLY",
  //             variableName: null,
  //             variableData: variableData,
  //             userCredential,
  //           },
  //         );
  //         let pendingMsg = { ...arr3[0] };
  //         if (
  //           pendingMsg.hasOwnProperty("text") &&
  //           message.length + 2 >= pendingMsg?.text?.durationVal
  //         ) {
  //           isSkip.current = true;
  //           setArr3((prev: any) => {
  //             const [_, ...pendings] = prev;
  //             return pendings;
  //           });
  //           setMessage((prevItems) => [...prevItems, pendingMsg]);
  //           return;
  //         } else {
  //           setCurrentQuestionIndex(questions.length - 1);
  //         }
  //       }
  //     }
  //   } catch (err) {
  //     console.log(err);
  //   } finally {
  //     isSkip.current = true;
  //   }
  // };

  async function typeWithDelay(text, delay = 50) {
    // Add the complete message first but mark it as streaming
    setMessage((prevItems: any) => {
      let tempMessage = [...prevItems];
      const newMessage = {
        author: ai,
        timestamp: new Date(),
        text: text,
        isStreaming: true,
        streamingIndex: 0,
      };
      tempMessage.push(newMessage);
      return tempMessage;
    });

    // Extract text content to get character count for animation
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = text;
    const textContent = tempDiv.textContent || tempDiv.innerText || "";
    const totalChars = textContent.length;

    // Animate the streaming effect by updating the streaming index
    await new Promise<void>((resolve) => {
      let currentIndex = 0;
      const interval = setInterval(() => {
        currentIndex += 2; // Show 2 characters at a time for smoother effect

        setMessage((prevItems: any) => {
          let tempMessage = [...prevItems];
          const lastMessage = tempMessage[tempMessage.length - 1];
          if (lastMessage && lastMessage.isStreaming) {
            lastMessage.streamingIndex = Math.min(currentIndex, totalChars);
            if (currentIndex >= totalChars) {
              lastMessage.isStreaming = false;
              delete lastMessage.streamingIndex;
            }
          }
          return [...tempMessage];
        });

        if (currentIndex >= totalChars) {
          clearInterval(interval);
          resolve();
        }
      }, delay);
    });
  }

  const handleStarterQues = async (reply: string) => {
    setResponseLoader(true);
    let handledByUserReply = false; // Flag to skip finally block's nudge
    try {
      if (!islast) {
        const isStaterQues = true;
        const isCategoryMode = !activeCat && botData?.aiStarterQues.isCat;
        if (isCategoryMode) {
          const catQuesAns = botData?.aiStarterQues.data.filter(
            (item) => item.cat === reply,
          );
          setStarterQues(catQuesAns);
          setActiveCat({ text: reply });
          return;
        }
        const ans = botData?.aiStarterQues?.data?.find(
          (item) => item.ques === reply,
        );

        if (ans?.answeredByAI) {
          // handleUserReply will create the room itself, so don't create it here
          // Also, handleUserReply manages its own loader and nudge
          handledByUserReply = true; // Mark that handleUserReply handles everything
          setMessage((prevItems) => [
            ...prevItems,
            {
              author: person,
              timestamp: new Date(),
              text: stripHTML(reply),
            },
          ]);

          // handleUserReply is async and handles its own loader/nudge
          handleUserReply(
            message?.length ? message?.length - 1 : 0,
            false,
            stripHTML(reply),
            false,
          );
          return; // Exit early - handleUserReply handles loader and nudge
        } else {
          // Only create room in this path, not when answeredByAI
          const roomCreationPromise =
            roomId === null && firstMessage.current.length !== 0
              ? createRoom()
              : Promise.resolve();

          await updateUIForStarterQuestion(stripHTML(reply), ans);

          resetStarterQuestionState();

          const [roomData] = await Promise.all([roomCreationPromise]);

          if (roomData) {
            handleRoomCreation(roomData);
          }

          // Save user question first to get/use the roomId
          const userQuestionResponse = await saveUserQuestion(
            stripHTML(reply),
            isStaterQues,
          );

          // If we got a new roomId from the first call, update it before the second call
          if (
            userQuestionResponse?.data?.roomId &&
            !roomId &&
            !roomID.current
          ) {
            roomID.current = userQuestionResponse.data.roomId;
            setRoomId(userQuestionResponse.data.roomId);
          }

          // Now save bot answer with the established roomId
          await saveBotAnswer(ans);

          handlePendingMessages();
        }
      }
    } catch (err) {
      console.log(err);
    } finally {
      isSkip.current = true;
      // Only reset loader and trigger nudge if NOT handled by handleUserReply
      if (!handledByUserReply) {
        setResponseLoader(false);
        triggerNudge(botData?.aiBotConfig?.nudgeConfig);
      }
    }
  };

  // Helper functions
  const createRoom = async () => {
    const response = await axiosPost(
      `/liveBot/saveAnswer?botId=${botId}&roomId=${roomId}&country=${formattedLocation}`,
      {
        botCustomId,
        nodeTitle: firstMessage.current[0].nodeTitle,
        drawFlowId: "1",
        userAnswer: "NO_REPLY",
        variableData,
        userCredential,
        nodeType: firstMessage.current[0].nodeType,
        contactId: contactData?._id,
        userData,
        chatSummary: {
          summary: chatSummary,
          nextSummaryStart: nextChatSummaryStart,
          pending: chatSummaryPending,
        },
        model: botData?.botModel,
      },
    );
    return response.data;
  };

  const updateUIForStarterQuestion = async (reply, ans) => {
    setMessage((prevItems) => [
      ...prevItems,
      {
        author: person,
        timestamp: new Date(),
        text: reply,
      },
      {
        author: ai,
        timestamp: new Date(),
        text: ans?.ans,
      },
    ]);

    // await typeWithDelay(ans?.ans, 30);
  };

  const resetStarterQuestionState = () => {
    setStarterQues(undefined);
    setActiveCat(undefined);
    setTempStore("");
    if (suggestedQuesAns) setSuggestedQuesAns(false);
  };

  const handleRoomCreation = (roomData) => {
    roomID.current = roomData.roomId;
    setRoomId(roomData.roomId);
    connectSocket(roomData.roomId);
    setContactData(roomData?.contact);
    window.localStorage.setItem("cbb-contact-id", roomData?.contact._id);
    firstMessage.current = [];
  };

  const saveUserQuestion = async (reply, isStaterQues) => {
    return axiosPost(
      `/liveBot/saveAnswer?botId=${botId}&roomId=${roomId ?? roomID.current}&country=${formattedLocation}&starterQues=${isStaterQues}`,
      {
        isAiBot: true,
        botCustomId,
        nodeTitle: environmentData.isAI
          ? message[message.length - 1]?.name === "ask_question" ||
            message[message.length - 1]?.name === "email" ||
            message[message.length - 1]?.name === "job" ||
            message[message.length - 1]?.name === "name" ||
            message[message.length - 1]?.name === "address" ||
            message[message.length - 1]?.name === "company" ||
            message[message.length - 1]?.name === "phone"
            ? removeCustomTags(message[message.length - 1].text.value)
            : removeCustomTags(message[message.length - 1].text)
          : (removeCustomTags(message[message.length - 1].text) ?? ""),
        drawFlowId: message[currentQuestionIndex]?.drawFlowId ?? 0,
        userAnswer: removeCustomTags(reply),
        variableName: null,
        variableData,
        userCredential,
        model: botData?.botModel,
      },
    );
  };

  const saveBotAnswer = async (ans) => {
    return axiosPost(
      `/liveBot/saveAnswer?botId=${botId}&roomId=${roomId ?? roomID.current}&country=${formattedLocation}`,
      {
        isAiBot: true,
        botCustomId,
        nodeTitle: typeof ans?.ans === "string" ? ans.ans : "",
        drawFlowId: message[currentQuestionIndex]?.drawFlowId ?? 0,
        userAnswer: "NO_REPLY",
        variableName: null,
        variableData,
        userCredential,
        model: botData?.botModel,
      },
    );
  };

  const handlePendingMessages = () => {
    const pendingMsg = arr3[0];
    if (
      pendingMsg?.hasOwnProperty("text") &&
      message.length + 2 >= pendingMsg?.text?.durationVal
    ) {
      isSkip.current = true;
      setArr3((prev) => prev.slice(1)); // More efficient than destructuring
      setMessage((prevItems) => [...prevItems, pendingMsg]);
    } else {
      setCurrentQuestionIndex(questions.length - 1);
    }
  };
  const handleNudgeMech = async (nudgeConfig: any, isFinal: boolean) => {
    try {
      if (chatHandlerRef.current === "crisp") return;
      let msgToSend = isFinal ? nudgeConfig?.msg : nudgeConfig?.finalMsg;
      setMessage((prevItems: any) => {
        const tempMessage = [...prevItems];
        tempMessage.push({
          author: ai,
          timestamp: new Date(),
          text: msgToSend,
        });
        return tempMessage;
      });
      if (roomId || roomID.current) {
        await axiosPost(
          `/liveBot/saveAnswer?botId=${botId}&roomId=${roomId ?? roomID.current}&country=${
            formattedLocation
          }`,
          {
            botCustomId: botCustomId,
            nodeTitle: msgToSend,
            drawFlowId: questions[currentQuestionIndex]?.drawFlowId ?? 0,
            userAnswer: "NO_REPLY",
            variableData: {},
            userCredential,
            isAiBot: true,
            model: botData?.botModel,
          },
        );
      }
    } catch (err) {
      console.log(err);
    }
  };

  const triggerNudge = async (nudgeConfig: any) => {
    // Don't trigger nudge while streaming is in progress (use ref for current value)
    if (isStreamingRef.current) return;

    if (chatHandlerRef.current == "ai" && nudgeConfig?.collectNudgeDetails) {
      clearTimeout(nudgeTimer3.current);
      if (nudgeConfig?.msg) {
        clearTimeout(nudgeTimer.current);
        clearTimeout(nudgeTimer2.current);
        nudgeTimer.current = setTimeout(
          () => {
            // Check again at execution time - streaming might have started
            if (isStreamingRef.current) return;
            const lastMessage = message[message.length - 1];
            if (!lastMessage?.isLoader) {
              handleNudgeMech(nudgeConfig, true);
            }
            clearTimeout(nudgeTimer.current);
          },
          (nudgeConfig?.durationUnit === "minutes"
            ? nudgeConfig?.duration * 60
            : nudgeConfig?.duration) * 1000,
        );

        if (nudgeConfig?.finalMsg) {
          clearTimeout(nudgeTimer2.current);
          nudgeTimer2.current = setTimeout(
            () => {
              // Check again at execution time - streaming might have started
              if (isStreamingRef.current) return;
              const lastMessage = message[message.length - 1];
              if (!lastMessage?.isLoader) {
                handleNudgeMech(nudgeConfig, false);
              }
              clearTimeout(nudgeTimer2.current);
            },
            (nudgeConfig?.durationUnit === "minutes"
              ? nudgeConfig?.duration * 60
              : nudgeConfig?.duration) *
              1000 *
              2,
          );
        }
      }
      nudgeTimer3.current = setTimeout(
        () => {
          // Check again at execution time - streaming might have started
          if (isStreamingRef.current) return;
          if (chatHandlerRef.current === "ai") setIsLast(true);
        },
        (() => {
          const duration1InSec =
            nudgeConfig?.durationUnit === "minutes"
              ? nudgeConfig?.duration * 60
              : nudgeConfig?.duration;

          const duration2InSec =
            nudgeConfig?.endSession?.durationUnit === "minutes"
              ? nudgeConfig?.endSession?.duration * 60
              : nudgeConfig?.endSession?.duration;

          return (
            ((nudgeConfig?.finalMsg ? duration1InSec * 2 : duration1InSec) +
              duration2InSec) *
            1000
          );
        })(),
      );
    }
  };

  const clearNudgeTimers = () => {
    if (botData?.aiBotConfig?.nudgeConfig?.collectNudgeDetails) {
      if (nudgeTimer?.current) clearTimeout(nudgeTimer.current);
      if (nudgeTimer2?.current) clearTimeout(nudgeTimer2.current);
      if (nudgeTimer3?.current) clearTimeout(nudgeTimer3.current);
    }
  };

  const handleRegenerate = async (retry: boolean) => {
    if (retry && regenerate.current) return;
    regenerate.current = true;
    setMessage((prevItems: any) => {
      const tempMessage = [...prevItems];
      const lastMessage = tempMessage[tempMessage.length - 1];
      if (lastMessage.author == ai) {
        tempMessage.pop();
        tempMessage.pop();
      } else {
        tempMessage.pop();
      }
      return tempMessage;
    });
    let retryMsgIndex;
    if (retry) {
      if (message[message.length - 1]?.author) {
        //user
        retryMsgIndex = 1;
      } else {
        if (message[message.length - 2]?.author) {
          //user
          retryMsgIndex = 2;
        } else {
          retryMsgIndex = 3;
        }
      }
    }
    await handleUserReply(
      message?.length ? message?.length - 1 : 0,
      false,
      message[message?.length - (retry ? retryMsgIndex : 1)]?.text,
      regenerate.current,
    );

    regenerate.current = false;
  };

  async function handlefetchResponseFromAI(aiReply, reply, fallback = false) {
    const aiChatCount = +(
      new RegExp(`chat_${botId}=(\\d+)`).exec(document.cookie)?.[1] ?? "0"
    );
    if (aiChatCount >= botData.aiBotConfig.usageLimit && !isPreviewModeChat) {
      (aiReply =
        QUESTION_LIMIT_REACHED_MESSAGE[
          botData?.aiBotConfig?.language?.toLowerCase()
        ] || QUESTION_LIMIT_REACHED_MESSAGE.english),
        setMessage((prevMessage) => [
          ...prevMessage,
          // {
          //   author: person,
          //   timestamp: new Date(),
          //   text: reply,
          // },
          {
            author: ai,
            timestamp: new Date(),
            text: aiReply,
          },
        ]);
      setIsLast(true);
      // await axiosPost(
      //   `/liveBot/saveAnswer?botId=${botId}&roomId=${roomId ?? roomID.current ?? nanoid()}&country=${formattedLocation}`,
      //   {
      //     isAiBot: true,
      //     botCustomId: botCustomId,
      //     nodeTitle: "",
      //     drawFlowId: message[currentQuestionIndex]?.drawFlowId ?? 0,
      //     userAnswer: reply,
      //     variableName: null,
      //     variableData: variableData,
      //     userCredential,
      //     userData,
      //     chatSummary: {
      //       summary: chatSummary,
      //       nextSummaryStart: nextChatSummaryStart,
      //       pending: chatSummaryPending,
      //     },
      //   }
      // ).catch(error => {
      //   console.error('DEBUG: Save Last Query Error', {
      //     error: error.message,
      //     status: error.response?.status,
      //     data: error.response?.data,
      //   });
      // });
      // return;
      return null;
    } else {
      const expires = new Date(Date.now());
      expires.setUTCHours(expires.getUTCHours() + 1, 0, 0, 0);

      document.cookie = `chat_${botId}=${
        aiChatCount + 1
      }; expires=${expires.toUTCString()}; path="/"; domain=${
        window.location.hostname
      }`;

      if (!fallback) {
        setMessage((prevItems) => [
          ...prevItems,
          {
            author: person,
            timestamp: new Date(),
            text: reply,
          },
          {
            author: ai,
            timestamp: new Date(),
            isLoader: true,
            text: "Loading...",
          },
        ]);
      }
      setResponseLoader(true);
      const { previousChat, totalMessages } = generatePreviousChat();

      queryRequestSignal.current = new AbortController();
      queryRequestId.current = nanoid();
      reasoning.stop(true);

      let _contactData = contactData;

      if (roomId === null && firstMessage.current.length !== 0) {
        const response = await axiosPost(
          `/liveBot/saveAnswer?botId=${botId}&roomId=${roomId}&country=${formattedLocation}`,
          {
            botCustomId: botCustomId,
            nodeTitle: firstMessage.current[0].nodeTitle,
            drawFlowId: "1",
            userAnswer: "NO_REPLY",
            variableData: variableData,
            userCredential: userCredential,
            nodeType: firstMessage.current[0].nodeType,
            contactId: contactData?._id,
            userData,
            chatSummary: {
              summary: chatSummary,
              nextSummaryStart: nextChatSummaryStart,
              pending: chatSummaryPending,
            },
            model: botData?.botModel,
          },
        );

        roomID.current = response.data.roomId;
        connectSocket(response.data.roomId);
        if (response.data?.contact) {
          setContactData(response.data.contact);
          setLocalContactData(response.data.contact);
        }
        window.localStorage.setItem(
          "cbb-contact-id",
          response.data?.contact?._id,
        );
        _contactData = response.data?.contact;

        firstMessage.current = [];
      }

      // Mark streaming as started and cancel any pending nudge timers
      isStreamingRef.current = true;
      clearTimeout(nudgeTimer.current);
      clearTimeout(nudgeTimer2.current);
      clearTimeout(nudgeTimer3.current);

      const response = await fetch(
        `${environmentData.SERVER_API_URL}/ai/query`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            queryRequestId: queryRequestId.current,
            query: reply,
            language: botData?.aiBotConfig?.language,
            includeSource: botData.showDataSource,
            namespace: botData.botCustomId,
            streaming: true,
            previousChat: previousChat,
            totalMessages: totalMessages,
            configId: botData.aiBotConfig.configId,
            botId: botData._id,
            botCreatedAt: botData.createdAt,
            updateTime: new Date(botData.updatedAt).getTime(),
            companyId: botData.companyId,
            debug: debug.current,
            showSteps: steps.current,
            ip: variableData?.ip,
            socketRoomId: socketRoomId.current,
            botModel: botData.botModel,
            humanAgentConfig: botData?.aiBotConfig?.humanAgentConfig ?? {},
            userResponseId: roomId ?? roomID.current,
            userData,
            contactId: _contactData?._id,
            isPreview: isPreviewModeChat,
            responseCacheControl: botData?.responseCacheControl,
            location: formattedLocation,
            customGoalsTriggered: customGoalsTriggered.current,
            creditId,
            isTrial,
            fromOutgrow,
            userPrompt: localStorage.getItem("userPrompt"),
            enableHyde: searchParams.get("hyde") === "true",
            maestroMode: searchParams.get("maestroMode"),
          }),
          signal: queryRequestSignal.current?.signal,
        },
      );
      const reader = (response.body ?? new ReadableStream<Uint8Array>())
        .pipeThrough(new TextDecoderStream() as any)
        .getReader();

      while (true) {
        let { value, done } = await reader.read();

        if (done === undefined || done) {
          // Mark streaming as done before triggering nudge
          isStreamingRef.current = false;

          if (!creditConsumedNotifiedRef.current) {
            creditConsumedNotifiedRef.current = true;
            console.log("[CREDIT_CONSUMED] postMessage fired to parent");
            window.parent.postMessage({ type: "CREDIT_CONSUMED" }, "*");
          }

          await triggerNudge(botData?.aiBotConfig?.nudgeConfig);
          setUsingToolLoader(false);

          if (showCalendlyBtn.current) {
            showCalendlyBtn.current = false;

            setMessage((prevItems: any) => {
              const tempMessage = structuredClone(prevItems);
              const lastMessage = tempMessage[tempMessage.length - 1];

              lastMessage.name = "show_schedule_call";

              return tempMessage;
            });
          }
          reasoning.stop(true);
          // Clear pending tool data after it's been attached to the message
          pendingToolData.current = {
            toolCalls: [],
            toolResults: [],
          };
          break;
        }

        const { ongoing: reasoningOngoing } = reasoning.handleStream(
          value as string,
        );
        if (reasoningOngoing) continue;

        // value = reasoning.sanitizeTags(value as string);
        aiReply += value;

        setMessage((prevItems: any) => {
          const tempMessage = structuredClone(prevItems);
          const lastMessage = tempMessage[tempMessage.length - 1];

          if (lastMessage.isLoader || lastMessage?.isTemp) {
            tempMessage.pop();
            tempMessage.push({
              author: ai,
              timestamp: new Date(),
              text: aiReply,
              aiResponse: true,
              toolCalls: pendingToolData.current.toolCalls,
              toolResults: pendingToolData.current.toolResults,
            });
          } else {
            if (lastMessage.author === ai) {
              lastMessage.text = aiReply;
            } else {
              tempMessage.push({
                author: ai,
                timestamp: new Date(),
                text: aiReply,
                aiResponse: true,
                toolCalls: pendingToolData.current.toolCalls,
                toolResults: pendingToolData.current.toolResults,
              });
            }
          }
          return tempMessage;
        });
      }
      return aiReply;
    }
  }

  async function handleUserReply(
    currentIndex,
    isPhone = false,
    replyMsg = newReply,
    regenerated,
  ) {
    try {
      setResponseCacheOptions(null);
      setSuggestedQuestionsData([]);
      creditConsumedNotifiedRef.current = false;
      // const currentDataSource = dataSource;
      setDataSource([]);
      dataSourceRef.current = []; // Clear ref as well
      setIsAnswerSaved(false);
      customGoalsMsg.current = "";
      newReplyLast.current = replyMsg;
      lastSourceQuery.current = "";
      // pendingSaveAnswer.current = null;

      setIsCustomProviderError(false);

      await triggerNudge(botData?.aiBotConfig?.nudgeConfig);
      // clearNudgeTimers();
      const reply = replyMsg;
      setNewReply("");
      if (!checkMsg) {
        setTempStore("");
        setStarterQues(undefined);
      }
      if (starterQues) {
        const updateMsgs = message.filter(
          (ele) => ele.text !== botData?.aiStarterQues?.initialMsg,
        );
        setMessage(updateMsgs);
        chatHandlerRef.current = "ai";
      }
      setIsAiResError(false);

      if (chatHandlerRef.current === "ai" || starterQues) {
        if (checkIndex) {
          const check = botData.aiBotConfig?.aiInitMessage;
          if (check) {
            chatHandlerRef.current = "web";
            setMessage((prevItems) => [
              ...prevItems,
              {
                author: person,
                timestamp: new Date(),
                text: reply,
              },
            ]);
            setCurrentQuestionIndex(questions.length - 1);
          } else {
            setMessage((prevItems) => [
              ...prevItems,
              {
                author: person,
                timestamp: new Date(),
                text: reply,
              },
            ]);
          }
          setCheckIndex(false);
        } else {
          let aiReply = "";
          try {
            aiReply = await handlefetchResponseFromAI(aiReply, reply, false);
            if (aiReply?.includes("<handoff>")) {
              const match = aiReply.match(/<handoff>([^<]*)<\/?handoff>/);
              const handoffData = JSON.parse(match?.[1] ?? "{}");

              if (handoffData?.isConnected) {
                handOffConnect(handoffData);
              } else {
                console.error("failed to connect to human agent", handoffData);
              }
            }

            if (aiReply === "") {
              setIsAiResError(true);

              const errMsg = `LiveCalc: got empty response from backend, query - ${reply}`;
              console.error(errMsg);
              trackError(new Error(errMsg));
            }
          } catch (err: any) {
            console.error(err);
            const isAborted =
              queryRequestSignal.current &&
              queryRequestSignal.current.signal.aborted === true;

            if (!isAborted) {
              setIsAiResError(true);
              // TypeError from fetch => CORS / network
              // You cannot get a status/body here in the browser.
              console.error("Network/CORS error:", err?.name || err);
              if (err instanceof Error) {
                trackError(
                  new Error(
                    `LiveCalc: while getting response, triggering fallback - msg: ${err.message}, code: ${(err as any).code ?? ""}, query - ${reply}`,
                  ),
                );
                try {
                  clearTimeout(responseTimer.current);
                  handleResponseTimer();
                  setMessage((pre) =>
                    produce(pre, (draft: any[]) => {
                      const lastMessage = draft.at(-1);
                      if (!lastMessage) return;

                      if (lastMessage.author === ai) {
                        draft.pop();
                      }
                      draft.push({
                        author: ai,
                        timestamp: new Date(),
                        isLoader: true,
                        text: "Loading...",
                      });
                    }),
                  );
                  aiReply = await handlefetchResponseFromAI(
                    aiReply,
                    reply,
                    true,
                  );
                } catch (error) {
                  console.error(
                    "Error occurred while fetching response from AI:",
                    error,
                  );
                  trackError(
                    new Error(
                      `LiveCalc: while getting fallback response - msg: ${err.message}, code: ${(err as any).code ?? ""}, query - ${reply}`,
                    ),
                  );
                }
              }
            }
          } finally {
            isStreamingRef.current = false; // Ensure streaming flag is reset on error/completion
            setResponseLoader(false);

            const isAborted =
              queryRequestSignal.current &&
              queryRequestSignal.current.signal.aborted === true;

            setMessage((pre) =>
              produce(pre, (draft: any[]) => {
                const lastMessage = draft.at(-1);
                if (!lastMessage) return;

                if (lastMessage.isLoader) {
                  draft.pop();
                } else if (isAborted) {
                  lastMessage.terminateByUser = true;

                  if (lastMessage.author === ai && draft.at(-2)) {
                    draft.at(-2).terminateByUser = true;
                  }
                }
              }),
            );

            console.log("[SaveAnswer Livebot] Sending tool data:", {
              toolCallsCount: pendingToolData.current.toolCalls.length,
              toolResultsCount: pendingToolData.current.toolResults.length,
              toolCalls: pendingToolData.current.toolCalls,
              toolResults: pendingToolData.current.toolResults,
            });

            axiosPost(
              `/liveBot/saveAnswer?botId=${botId}&roomId=${roomId ?? roomID.current}&country=${formattedLocation}`,
              {
                isAiBot: true,
                botCustomId: botCustomId,
                nodeTitle: aiReply ?? "",
                drawFlowId: message[currentQuestionIndex]?.drawFlowId ?? 0,
                userAnswer: reply,
                variableName: null,
                variableData: variableData,
                userCredential,
                userData,
                regenerated,
                sources: dataSourceRef.current,
                chatSummary: {
                  summary: chatSummary,
                  nextSummaryStart: nextChatSummaryStart,
                  pending: chatSummaryPending,
                },
                toolCalls: pendingToolData.current.toolCalls,
                toolResults: pendingToolData.current.toolResults,
                ...(isAborted && {
                  terminateByUser: isAborted,
                }),
                model: botData?.botModel,
              },
            )
              .then((botAnswer) => {
                setRoomId(roomID.current || botAnswer.data.roomId);
                if (botAnswer.data.contact) {
                  setContactData(botAnswer.data.contact);
                  setLocalContactData(botAnswer.data.contact);
                }

                if (botAnswer.data.answerId) {
                  setIsAnswerSaved(true);
                  setMessage((prevItems: any) => {
                    const tempMessage = structuredClone(prevItems);

                    const lastMessage = tempMessage[tempMessage.length - 1];

                    if (lastMessage && lastMessage.aiResponse) {
                      lastMessage.sources = dataSourceRef.current;
                      lastMessage.id = botAnswer.data.answerId;
                    }

                    return tempMessage;
                  });
                }
              })
              .catch((err) => {
                console.log(err);
              });

            return;
          }
        }

        const variableDataToSend = {};
        const varName = message[currentIndex]?.completeData?.variableName;
        if (varName) variableDataToSend[varName] = reply;

        await axiosPost(
          `/liveBot/saveAnswer?botId=${botId}&roomId=${roomId}&country=${formattedLocation}`,
          {
            isAiBot: true,
            botCustomId: botCustomId,
            nodeTitle: environmentData.isAI
              ? message[message.length - 1]?.name === "ask_question" ||
                message[message.length - 1]?.name === "email" ||
                message[message.length - 1]?.name === "job" ||
                message[message.length - 1]?.name === "name" ||
                message[message.length - 1]?.name === "address" ||
                message[message.length - 1]?.name === "company" ||
                message[message.length - 1]?.name === "phone"
                ? message[message.length - 1].text.value
                : message[message.length - 1].text
              : (message[message.length - 1].text ?? ""),
            drawFlowId: message[currentQuestionIndex]?.drawFlowId ?? 0,
            userAnswer: reply,
            sources: dataSourceRef.current,
            variableName: varName,
            variableData: variableDataToSend,
            userCredential,
            model: botData?.botModel,
          },
        );
      } else if (chatHandlerRef.current === "intercom") {
        setMessage((prevItems) => [
          ...prevItems,
          {
            author: person,
            timestamp: new Date(),
            text: reply,
          },
        ]);

        if (!handoffData.current.isConnected)
          throw new Error("Agent not connected");

        await axiosPost("/integration/message/intercom", {
          configId: handoffData.current.configId,
          contactId: handoffData.current.payload.intercomUser?.id,
          conversationId: handoffData.current.payload.conversationId,
          message: reply,
        });

        await axiosPost(
          `/liveBot/saveAnswer?botId=${botId}&roomId=${roomId}&country=${formattedLocation}`,
          {
            isAiBot: true,
            botCustomId: botCustomId,
            nodeTitle: "",
            drawFlowId: message[currentQuestionIndex]?.drawFlowId ?? 0,
            userAnswer: reply,
            variableName: null,
            variableData: variableData,
            userCredential,
            model: botData?.botModel,
          },
        );
      } else if (chatHandlerRef.current === "zendesk") {
        setMessage((prevItems) => [
          ...prevItems,
          {
            author: person,
            timestamp: new Date(),
            text: reply,
          },
        ]);

        if (!handoffData.current.isConnected)
          throw new Error("Agent not connected");

        await axiosPost("/integration/message/zendesk", {
          configId: handoffData.current.configId,
          authorId: String(handoffData.current.payload.zendeskUser?.id),
          ticketId: handoffData.current.payload.conversationId,
          message: reply,
        });

        await axiosPost(
          `/liveBot/saveAnswer?botId=${botId}&roomId=${roomId}&country=${formattedLocation}`,
          {
            isAiBot: true,
            botCustomId: botCustomId,
            nodeTitle: "",
            drawFlowId: message[currentQuestionIndex]?.drawFlowId ?? 0,
            userAnswer: reply,
            variableName: null,
            variableData: variableData,
            userCredential,
            model: botData?.botModel,
          },
        );
      } else if (chatHandlerRef.current === "cbb-live-agent") {
        setMessage((prevItems) => [
          ...prevItems,
          {
            author: person,
            timestamp: new Date(),
            text: reply,
          },
        ]);

        if (!handoffData.current.isConnected)
          throw new Error("Agent not connected");

        await axiosPost("/integration/message/cbb-live-agent", {
          conversationId: handoffData.current.payload.conversationId,
          message: reply,
        });

        await axiosPost(
          `/liveBot/saveAnswer?botId=${botId}&roomId=${roomId}&country=${formattedLocation}`,
          {
            isAiBot: true,
            botCustomId: botCustomId,
            nodeTitle: "",
            drawFlowId: message[currentQuestionIndex]?.drawFlowId ?? 0,
            userAnswer: reply,
            variableName: null,
            variableData: variableData,
            userCredential,
            model: botData?.botModel,
          },
        );
      } else if (chatHandlerRef.current === "crisp") {
        setMessage((prevItems) => [
          ...prevItems,
          {
            author: person,
            timestamp: new Date(),
            text: reply,
          },
        ]);

        if (!handoffData.current.isConnected)
          throw new Error("Agent not connected");

        await axiosPost("/integration/message/crisp", {
          configId: handoffData.current.configId,
          conversationId: handoffData.current.payload.conversationId,
          message: reply,
        });

        await axiosPost(
          `/liveBot/saveAnswer?botId=${botId}&roomId=${roomId}&country=${formattedLocation}`,
          {
            isAiBot: true,
            botCustomId: botCustomId,
            nodeTitle: "",
            drawFlowId: message[currentQuestionIndex]?.drawFlowId ?? 0,
            userAnswer: reply,
            variableName: null,
            variableData: variableData,
            userCredential,
            model: botData?.botModel,
          },
        );
      } else if (!islast) {
        const tempMessage = [...message];
        tempMessage[message.length - 1].hasAnswered = true;
        setMessage(tempMessage);

        setMessage((prevItems) => [
          ...prevItems,
          {
            author: person,
            timestamp: new Date(),
            text: reply,
          },
        ]);
        // let pendingMsg = { ...arr3[0] };
        // let pendingQues = { ...arr[0] };
        // if (
        //   arr2.length === 0 &&
        //   checkMsg &&
        //   ((pendingMsg.hasOwnProperty("text") &&
        //     message.length + 1 < pendingMsg?.text?.durationVal) ||
        //     !pendingMsg.hasOwnProperty("text"))
        // ) {
        //   setStarterQues(tempStore);
        //   setMessage((prevItems) => [
        //     ...prevItems,
        //     {
        //       author: ai,
        //       timestamp: new Date(),
        //       text: botData?.aiStarterQues?.initialMsg,
        //     },
        //   ]);
        //   setCheckMsg(false);
        //   const variableDataToSend = {};
        //   const varName = message[currentIndex]?.completeData?.variableName;
        //   if (varName) variableDataToSend[varName] = reply;

        //   const userCredentialData = { ...userCredential };
        //   if (message[message.length - 1]?.name === "email")
        //     userCredentialData.email = reply;
        //   if (message[message.length - 1]?.name === "phone")
        //     userCredentialData.phone = reply;
        //   if (message[message.length - 1]?.name === "username")
        //     userCredentialData.name = reply;
        //   // if (message[message.length - 1]?.name === "salesForce")
        //   //   userCredentialData.salesForce = reply;
        //   setUserCredential(userCredentialData);

        //   const response = await axiosPost(
        //     `/liveBot/saveAnswer?botId=${botId}&roomId=${roomId}&country=${variableData?.location}`,
        //     {
        //       isAiBot: true,
        //       botCustomId: botCustomId,
        //       nodeTitle: environmentData.isAI
        //         ? message[message.length - 1]?.name === "ask_question" ||
        //           message[message.length - 1]?.name === "email" ||
        //           message[message.length - 1]?.name === "job" ||
        //           message[message.length - 1]?.name === "name" ||
        //           message[message.length - 1]?.name === "address" ||
        //           message[message.length - 1]?.name === "company" ||
        //           message[message.length - 1]?.name === "phone"
        //           ? message[message.length - 1].text.value
        //           : message[message.length - 1].text
        //         : (message[message.length - 1].text ?? ""),
        //       drawFlowId: message[currentIndex]?.completeData?.drawFlowId ?? 0,
        //       userAnswer: reply,
        //       variableName: varName,
        //     },
        //   );

        //   return;
        // } else if (
        //   (pendingQues.hasOwnProperty("text") && arr2.length === 0) ||
        //   (pendingMsg.hasOwnProperty("text") &&
        //     message.length + 1 >= pendingMsg?.text?.durationVal &&
        //     arr2.length === 0)
        // ) {
        //   if (message.length + 1 >= pendingMsg?.text?.durationVal) {
        //     isSkip.current = true;
        //     setArr3((prev: any) => {
        //       const [_, ...pendings] = prev;
        //       return pendings;
        //     });
        //     setMessage((prevItems) => [...prevItems, pendingMsg]);
        //   } else if (arr && arr.length > 0) {
        //     isSkip.current = true;
        //     setArr((prev: any) => {
        //       const [_, ...pendings] = prev;
        //       return pendings;
        //     });
        //     setMessage((prevItems) => [...prevItems, pendingQues]);
        //   }
        //   const variableDataToSend = {};
        //   const varName = message[currentIndex]?.completeData?.variableName;
        //   if (varName) variableDataToSend[varName] = reply;

        //   const userCredentialData = { ...userCredential };
        //   if (message[message.length - 1]?.name === "email")
        //     userCredentialData.email = reply;
        //   if (message[message.length - 1]?.name === "phone")
        //     userCredentialData.phone = reply;
        //   if (message[message.length - 1]?.name === "username")
        //     userCredentialData.name = reply;
        //   // if (message[message.length - 1]?.name === "salesForce")
        //   //   userCredentialData.salesForce = reply;
        //   setUserCredential(userCredentialData);

        //   await axiosPost(
        //     `/liveBot/saveAnswer?botId=${botId}&roomId=${roomId}&country=${variableData?.location}`,
        //     {
        //       isAiBot: true,
        //       botCustomId: botCustomId,
        //       nodeTitle:
        //         message[message.length - 1]?.name === "ask_question" ||
        //         message[message.length - 1]?.name === "email" ||
        //         message[message.length - 1]?.name === "job" ||
        //         message[message.length - 1]?.name === "name" ||
        //         message[message.length - 1]?.name === "address" ||
        //         message[message.length - 1]?.name === "company" ||
        //         message[message.length - 1]?.name === "phone"
        //           ? message[message.length - 1].text.value
        //           : (message[message.length - 1].text ?? ""),
        //       drawFlowId: message[currentIndex]?.completeData?.drawFlowId ?? 0,
        //       userAnswer: reply,
        //       variableName: varName,
        //       variableData: variableDataToSend,
        //       userCredential: userCredentialData,
        //     },
        //   );

        //   return;
        // } else if (startLiveChat) {
        //   if (!getCookie("senderId")) {
        //     setCookie("senderId", userCredential?.id);
        //   }
        //   const waitingForReply = structuredClone([...message].slice(-1)[0]);
        //   if (waitingForReply?.author !== 0) {
        //     waitingForReply.text = "";
        //   }

        //   const tempDrawFlowId =
        //     (startLiveChat
        //       ? liveChatNode?.drawFlowId
        //       : questions[currentQuestionIndex]?.drawFlowId) ??
        //     questions[currentQuestionIndex]?.drawFlowId;

        //   const data = {
        //     userAnswer: replyMsg,
        //     nodeTitle: waitingForReply.text.value ?? waitingForReply?.text,
        //     date: waitingForReply.timestamp,
        //     drawFlowId: tempDrawFlowId,
        //     isAiBot: false,
        //   };

        //   liveChatSocketRef.current.emit("userReply", {
        //     data,
        //     senderId,
        //   });
        // }
        const variableDataToSend = {};
        const varName = message[currentIndex]?.completeData?.variableName;
        if (varName) variableDataToSend[varName] = reply;

        const userCredentialData = { ...userCredential };
        if (message[message.length - 1]?.name === "email")
          userCredentialData.email = reply;
        if (message[message.length - 1]?.name === "phone")
          userCredentialData.phone = reply;
        if (message[message.length - 1]?.name === "username")
          userCredentialData.name = reply;
        setUserCredential(userCredentialData);

        let newNodeTitle = structuredClone([...message].slice(-1)[0]);

        if (startLiveChat && newNodeTitle?.author !== 0) {
          newNodeTitle.text = "";
        }

        const tempDrawFlowId =
          (startLiveChat
            ? liveChatNode?.drawFlowId
            : questions[currentQuestionIndex]?.drawFlowId) ??
          questions[currentQuestionIndex]?.drawFlowId;

        axiosPost(
          `/liveBot/saveAnswer?botId=${botId}&roomId=${roomId}&country=${formattedLocation}&isLiveChat=${startLiveChat}`,
          {
            botCustomId: botCustomId,
            nodeTitle: newNodeTitle.text.value ?? newNodeTitle.text ?? "",
            drawFlowId: tempDrawFlowId,
            userAnswer: reply,
            sources: dataSourceRef.current,
            variableName: varName,
            variableData: variableDataToSend,
            userCredential: userCredentialData,
            isAiBot: true,
            model: botData?.botModel,
          },
        ).then((response) => {
          if (roomId === null) {
            setRoomId(response.data.roomId);
          }
          if (!startLiveChat) {
            if (
              questions[currentQuestionIndex]?.outputs[0].connections !==
              undefined
            ) {
              let currentIndex = questions.findIndex(
                (x) =>
                  x.drawFlowId ===
                  questions[currentQuestionIndex]?.outputs[0].connections.node,
              );
              setCurrentQuestionIndex(currentIndex);
            } else {
              if (
                questions[currentQuestionIndex]?.outputs[0]?.connections !==
                undefined
              ) {
                let currentIndex = questions.findIndex(
                  (x) =>
                    x.drawFlowId ===
                    questions[currentQuestionIndex]?.outputs[0].connections
                      .node,
                );
                setCurrentQuestionIndex(currentIndex);
              } else {
                setIsLast(true);
              }
            }
          }
        });
      } else {
        // Add user message first
        setMessage((prevItems) => [
          ...prevItems,
          {
            author: person,
            timestamp: new Date(),
            text: reply,
          },
        ]);

        // Store the error message and set a timeout before showing it
        // This allows time for cooldown events to arrive first
        const errorMessage =
          ERROR_CONNECTION_ISSUE[
            botData?.aiBotConfig?.language?.toLowerCase()
          ] || ERROR_CONNECTION_ISSUE.english;

        pendingErrorMessage.current = errorMessage;

        // Clear any existing timeout
        if (errorTimeoutRef.current) {
          clearTimeout(errorTimeoutRef.current);
        }

        // Set timeout to show error message after 1 second if no cooldown event arrives
        // This gives time for cooldown and other error types to be processed first
        errorTimeoutRef.current = setTimeout(() => {
          if (
            pendingErrorMessage.current &&
            !cooldownActive &&
            !cooldownMessage
          ) {
            setMessage((prevItems) => [
              ...prevItems,
              {
                author: ai,
                timestamp: new Date(),
                isLoader: true,
                text: pendingErrorMessage.current,
              },
            ]);
          }
          pendingErrorMessage.current = "";
          errorTimeoutRef.current = null;
        }, 1000);
      }
    } catch (err) {
      setMessage((prevItems: any) => {
        if (prevItems[prevItems.length - 1]?.isLoader) {
          const tempMessage = structuredClone(prevItems);
          tempMessage.pop();
          return tempMessage;
        }
        return prevItems;
      });
      console.log(err);
    } finally {
      isSkip.current = true;
      setUsingToolLoader(false);
    }
  }

  interface Conversation {
    role: "user" | "assistant" | "tool";
    content: string | any[]; // Can be string or array for tool calls/results
  }

  function generatePreviousChat() {
    const SUMMARY_THRESHOLD = 30; // after how many message summarization will be triggered

    const conversation: Conversation[] = [];

    for (const data of message) {
      if (data.terminateByUser) continue;

      const content = data?.text?.value ?? data?.text;

      // Add the main text message
      conversation.push({
        role: data.author ? "user" : "assistant",
        content: content,
      });

      // If this is an assistant message with tool calls, add them
      if (!data.author && data.toolCalls && data.toolCalls.length > 0) {
        const formattedToolCalls = data.toolCalls.map((tc: any) => ({
          type: "tool-call",
          toolCallId: tc.toolCallId,
          toolName: tc.toolName,
          input: tc.input || tc.args,
        }));

        conversation.push({
          role: "assistant",
          content: formattedToolCalls,
        });

        // If there are tool results, add them as well
        if (data.toolResults && data.toolResults.length > 0) {
          const formattedToolResults = data.toolResults.map((tr: any) => ({
            type: "tool-result",
            toolCallId: tr.toolCallId,
            toolName: tr.toolName || tr.tool,
            output: tr.output || tr.result,
          }));

          conversation.push({
            role: "tool",
            content: formattedToolResults,
          });
        }
      }
    }

    if (conversation.length - nextChatSummaryStart >= SUMMARY_THRESHOLD) {
      summarizeChat(
        conversation.slice(nextChatSummaryStart),
        conversation.length,
      );
    }

    const previousChat = conversation.slice(nextChatSummaryStart);

    if (chatSummary) {
      previousChat.unshift({
        role: "assistant",
        content: chatSummary,
      });
    }

    return { previousChat, totalMessages: conversation.length };
  }

  async function summarizeChat(
    conversation: Conversation[],
    conversationLen: number,
  ) {
    if (chatSummaryPending) return;

    try {
      if (chatSummary) {
        conversation.unshift({
          role: "assistant",
          content: chatSummary,
        });
      }

      const res = await axiosPost("/ai/summarize-chat", {
        conversation,
        botCustomId: botData.botCustomId,
        updateTime: new Date(botData.updatedAt).getTime(),
        companyId: botData.companyId,
      });

      setChatSummary(res.data.data.summary ?? "");
      setNextChatSummaryStart(conversationLen);
      setScrapedData(mergeData(scrapedData, res.data.data.scrappedData));
    } catch (err) {
      console.log("Chat summary: ", err);
    } finally {
      setChatSummaryPending(false);
    }
  }

  const handleEnterSubmit = (
    currentIndex,
    isPhone = false,
    isEmail = false,
  ) => {
    setShowError(true);
    if (!newReply || !isReplyValid) return;
    handleUserReply(currentIndex, isPhone, newReply, regenerate.current);
    setShowError(false);
  };

  const handleSendReplyButton = (event: any = false) => {
    setResponseCacheOptions(null);
    if (debounce.current) return;
    debounce.current = true;
    setTimeout(() => {
      debounce.current = false;
    }, 1000);
    if (event) event.preventDefault();
    if (!newReply.trim().length) return;
    if (message[message.length - 1]?.isLoader) return;

    if (!islast) {
      const lastIndex = message.length ? message.length - 1 : 0;
      if (
        message[lastIndex]?.name == "email" &&
        message[lastIndex]?.text?.isValidate &&
        isSkip.current
      ) {
        axiosPost("/liveBot/emailValidation", { newReply }).then((res) => {
          if (!res.data.status) {
            setCheckValidate(true);
            setError(
              EMAIL_VALIDATION_MESSAGE[
                botData?.aiBotConfig?.language?.toLowerCase()
              ] || EMAIL_VALIDATION_MESSAGE.english,
            );
            return;
          }
          setCheckValidate(false);
          setError("");
          handleEnterSubmit(lastIndex, false);
        });
      } else {
        handleEnterSubmit(lastIndex, false);
      }
    }
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "42px";
    }
  };

  const debounce = useRef<any>();

  const handleReaction = async (msg, reaction) => {
    try {
      setMessage((prevItems) =>
        prevItems.map((item) =>
          item.id === msg.id ? { ...item, reaction: reaction } : item,
        ),
      );
      // reaction true means helpful & vice-versa
      socket.emit("response-reaction", {
        botCustomId,
        cacheResponseId: responseCacheOptions?.id,
        reaction: reaction,
      });
    } catch (err) {
      console.log(err);
    }
  };

  function adjustTextareaHeight() {
    const textarea = textareaRef.current;
    if (textarea) {
      const baseHeight = 42;
      const maxHeight = 100;
      textarea.style.height = `${baseHeight}px`;

      let finalHeight = baseHeight;

      if (textarea.value.trim()) {
        if (textarea.scrollHeight > baseHeight) {
          finalHeight = Math.min(textarea.scrollHeight, maxHeight);
          textarea.style.height = `${finalHeight}px`;
        }
      }
      const minRadius = 20;
      const maxRadius = 100;
      const ratio = (finalHeight - baseHeight) / (maxHeight - baseHeight);
      const newRadius = Math.max(
        minRadius,
        maxRadius - ratio * (maxRadius - minRadius),
      );

      setBorderRadius(newRadius);
    }
  }

  function handleButtomInput(event: any, isCheckBox: boolean = false) {
    const value = event.target.value;
    setNewReply(value);
    if (botData?.aiBotConfig?.showTypingSuggestions) sendUserQuery(value);
    adjustTextareaHeight();
  }

  const [currentElem, setCurrentElem] = useState<any>(null);

  const stopScroll = () => {
    if (currentElem) {
      const wrapperElement = document.querySelector(
        ".cbb-full-screen-preview-wrapper, .cbb-embed-preview-wrapper",
      ) as HTMLElement;

      if (wrapperElement) {
        const wrapperRect = wrapperElement.getBoundingClientRect();
        const elemRect = currentElem.getBoundingClientRect();
        const relativeTop =
          elemRect.top - wrapperRect.top + wrapperElement.scrollTop - 1000;
        wrapperElement.scrollTo({
          top: relativeTop,
          behavior: "smooth",
        });
      } else {
        const top =
          currentElem.getBoundingClientRect().top + window.scrollY - 1000;
        window.scrollTo({
          top,
          behavior: "smooth",
        });
      }
    }
  };

  function connectSocket(roomId: string) {
    socket.connect();

    socket.on("connect", function () {
      socket.emit("join", {
        roomId: socketRoomId.current,
        senderId: roomId,
      });
    });
  }

  useEffect(() => {
    const lastMessage = message[message.length - 1];
    if (
      lastMessage?.aiResponse &&
      lastMessage?.sources &&
      isAnswerSaved &&
      dataSource.length > 0
    ) {
      setDataSource([]);
      dataSourceRef.current = [];
    }
  }, [message, isAnswerSaved]);

  useEffect(() => {
    const handleContactUpdate = (data: any) => {
      if (data && contactData && data._id === contactData._id) {
        // preserve the fieldData structure merge if needed, but usually full object is sent
        setContactData(data);
        setLocalContactData(data);
      }
    };

    socket.on("contactUpdate", handleContactUpdate);

    return () => {
      socket.off("contactUpdate", handleContactUpdate);
    };
  }, [contactData]);

  useEffect(() => {
    return () => {
      socket.disconnect();
    };
  }, []);

  // Cleanup timeout on component unmount
  useEffect(() => {
    return () => {
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
        errorTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    window.addEventListener("beforeunload", function (event) {
      if (!embed) event.preventDefault();
      if (!socket) return;
      socket.disconnect();
    });
  }, []);

  async function handOffConnect(data) {
    handoffData.current = data;
    chatHandlerRef.current = data.handoffType;
    clearNudgeTimers();

    socket.emit("join-human-agent", {
      roomId: `${data.handoffType}_${data.payload.conversationId}`,
    });
  }

  useEffect(() => {
    async function handleScheduleCallConnect(data) {
      const { name, email, config } = data;
      setUserCredential((prev) => ({
        ...prev,
        name,
        email,
      }));
      if (!config.isEnable) return;

      setCalendlyConfig(config);
      showCalendlyBtn.current = true;
    }

    function handleVariableData(data) {
      setScrapedData(mergeData(scrapedData, data));
    }

    async function handleShowSources(data) {
      const { sources, type: sourceType = "vectorDb" } = data;
      // const visitedName = new Set<string>();
      let _dataSource: any[] = [];

      if (lastSourceQuery.current === newReplyLast.current) {
        _dataSource = [...dataSourceRef.current];
      } else {
        lastSourceQuery.current = newReplyLast.current;
        _dataSource = [];
      }

      function extractLogo(content: string): string | undefined {
        const imgRegex = /https?:\/\/[^\s'")]+?\.(png|jpg|jpeg|gif)/gi;
        const matches = content.match(imgRegex);
        if (!matches) return undefined;

        // Prefer images with 'logo' or 'brand' in the path
        const preferred = matches.find((url) => /logo|brand/i.test(url));

        return preferred || matches[0]; // fallback to first image if no match
      }

      if (sources.length) {
        const nameIndexMap = new Map<string, number>();

        // Pre-populate map from existing sources
        _dataSource.forEach((item, index) => {
          if (item.name) {
            nameIndexMap.set(item.name, index);
          }
        });

        for (const data of sources) {
          const raw = data?.name ?? data?.url;
          let name: string = sourceType == "web" ? data?.title : raw;
          let link: string = raw;
          let type: "link" | "txt" = sourceType == "web" ? "link" : "txt";

          if (
            sourceType == "vectorDb" &&
            typeof raw === "string" &&
            raw.startsWith("https")
          ) {
            type = "link";
            const url = new URL(raw);
            const segments = url.pathname.split("/").filter(Boolean);
            name =
              segments.length > 0
                ? segments[segments.length - 1]
                : url.hostname;
            link = raw;
          }

          const logo = extractLogo(data.content || "");

          if (nameIndexMap.has(name)) {
            const existingIndex = nameIndexMap.get(name)!;
            const existingData = _dataSource[existingIndex];

            _dataSource[existingIndex] = {
              ...existingData,
              content: `${existingData.content ?? ""}\n${data.content ?? ""}`,
              link: existingData.link || link,
              logo: existingData.logo || logo,
            };
          } else {
            const newItem = {
              ...data,
              name,
              link,
              type,
              logo,
            };
            nameIndexMap.set(name, _dataSource.length);
            _dataSource.push(newItem);
          }
        }

        setDataSource(_dataSource);
        dataSourceRef.current = _dataSource; // Update ref immediately
      }
    }

    async function handleShowDebugData(data) {
      console.log(data);
    }

    function usingTool(data) {
      const isCurrentRequestAborted =
        queryRequestSignal?.current?.signal?.aborted ?? false;
      if (isCurrentRequestAborted) return;

      console.log("Executing tool:", data);
      if (data?.type === "queryVectorDatabase") {
        setUsingToolLoader(true);
      }
    }

    function handleClearResponseTimeout(data) {
      console.log("Clearing response timeout from backend signal", data);
      if (responseTimer.current) {
        clearTimeout(responseTimer.current);
        responseTimer.current = null;
      }
      handleResponseTimer();
    }

    function handleShowRetry(data) {
      console.log("Retry exhausted: ", data);
      if (data.forUser) {
        // Clear any pending error message timeout since we're showing a specific error
        if (errorTimeoutRef.current) {
          clearTimeout(errorTimeoutRef.current);
          errorTimeoutRef.current = null;
        }
        pendingErrorMessage.current = "";

        setCooldownActive(true);
        setIsCustomProviderError(true);
        setCooldownMessage(
          data.errorCode == 529 || data.errorCode == 429
            ? ERROR_529[botData?.aiBotConfig?.language?.toLowerCase()] ||
                ERROR_529.english
            : ERROR_CONNECTION_ISSUE[
                botData?.aiBotConfig?.language?.toLowerCase()
              ] || ERROR_CONNECTION_ISSUE.english,
        );
        setMessage((pre) =>
          produce(pre, (draft) => {
            if (draft.length === 0) return;
            if (!draft.at(-1).isLoader) return;
            draft.pop();
          }),
        );
      }
      if (!data.isCustomProvider) {
        setResponseLoader(false);
        setIsAiResError(true);
        setMessage((pre) =>
          produce(pre, (draft) => {
            if (draft.length === 0) return;
            if (!draft.at(-1).isLoader) return;
            draft.pop();
          }),
        );
      }
      if (data.canRetry) {
        setCooldownActive(false);
        setCooldownRemainingTime(0);
        setCooldownMessage("");
        setIsCustomProviderError(false);
      } else if (data.isCustomProvider) {
        setIsCustomProviderError(true);
        setCooldownMessage(
          data.message ||
            WAIT_BEFORE_TRYING_MESSAGE[
              botData?.aiBotConfig?.language?.toLowerCase()
            ] ||
            WAIT_BEFORE_TRYING_MESSAGE.english,
        );
        setMessage((pre) =>
          produce(pre, (draft) => {
            if (draft.length === 0) return;
            if (!draft.at(-1).isLoader) return;
            draft.pop();
          }),
        );
      }
      const errMessage = data.errMessage ?? data.message ?? "";
      trackError(
        new Error(
          `live-calc: error from backend - ${errMessage}, query - ${newReplyLast.current}`,
        ),
      );
    }

    function handleShowReactionOrImprove({ id, preserved, answered }) {
      setResponseCacheOptions({ id, preserved, answered });
    }

    function handleSuggestedQuestions(data) {
      if (data && data.length > 0 && !responseLoader && !usingToolLoader)
        setSuggestedQuestionsData(data);
    }

    function handleCUstomGoalsTriggered(data) {
      customGoalsTriggered.current = true;
      customGoalsMsg.current = data.message;
    }

    socket.on("aiToolCalls", (data) => {
      console.log("Received tool calls:", data);
      pendingToolData.current = {
        toolCalls: data.toolCalls || [],
        toolResults: data.toolResults || [],
      };

      // Immediately update the last assistant message with tool data
      setMessage((prevItems) => {
        const tempMessage = structuredClone(prevItems);
        const lastMessage = tempMessage[tempMessage.length - 1];

        // Only update if the last message is from the assistant
        if (lastMessage && lastMessage.author === 0) {
          lastMessage.toolCalls = data.toolCalls || [];
          lastMessage.toolResults = data.toolResults || [];
          console.log("Attached tool data to message:", {
            toolCalls: lastMessage.toolCalls,
            toolResults: lastMessage.toolResults,
          });
        }

        return tempMessage;
      });
    });

    async function handleHumanAgentMessage(data) {
      if (processedAgentMsg.current.has(data.commentId)) {
        return;
      }
      processedAgentMsg.current.add(data.commentId);
      if (chatHandlerRef.current === "ai")
        chatHandlerRef.current = data.agentType;
      if (
        data.agentType === "zendesk" &&
        (!data.authorId ||
          !handoffData.current.payload.zendeskUser?.userId ||
          data.authorId === handoffData.current.payload.zendeskUser.userId)
      ) {
        return;
      }
      const agentMessage = parseMessage(data.message);

      setMessage((prevItems) => [
        ...prevItems,
        {
          author: ai,
          timestamp: new Date(),
          text: agentMessage,
          agent: data.agent,
        },
      ]);
    }

    async function humanAgentDisconnect() {
      chatHandlerRef.current = "ai";
      setTimeout(() => triggerNudge(botData?.aiBotConfig?.nudgeConfig), 0);
      setMessage((prevItems) => [
        ...prevItems,
        {
          author: person,
          timestamp: new Date(),
          text: "Do not connect me to a human agent, do not make any tool calls involving a human agent, and do not mention anything about disconnecting or avoiding a human agent in your response.", //"disconnect me from the human agent and dont make tool call to human agent and even dont mention about that i have dissconnected with agent in the response",
          name: "agent-end",
        },
      ]);
    }

    socket.on("showSources", handleShowSources);
    socket.on("showDebugData", handleShowDebugData);
    socket.on("human-agent-message", handleHumanAgentMessage);
    socket.on("handoff-disconnect", humanAgentDisconnect);
    socket.on("schedule-call-connect", handleScheduleCallConnect);
    socket.on("variable-data", handleVariableData);
    socket.on("executing-tool", usingTool);
    socket.on("show-retry-exceeded", handleShowRetry);
    socket.on("response-cache-options", handleShowReactionOrImprove);
    socket.on("suggested-questions", handleSuggestedQuestions);
    socket.on("cutom-goals-triggered", handleCUstomGoalsTriggered);
    socket.on("cooldown-started", handleCooldownStarted);
    socket.on("cooldown-status", handleCooldownStatus);
    socket.on("clear-response-timeout", handleClearResponseTimeout);

    return () => {
      socket.off("aiToolCalls");
      socket.off("human-agent-message", handleHumanAgentMessage);
      socket.off("handoff-disconnect", humanAgentDisconnect);
      socket.off("showSources", handleShowSources);
      socket.off("showDebugData", handleShowDebugData);
      socket.off("schedule-call-connect", handleScheduleCallConnect);
      socket.off("variable-data", handleVariableData);
      socket.off("executing-tool", usingTool);
      socket.off("show-retry-exceeded", handleShowRetry);
      socket.off("response-cache-options", handleShowReactionOrImprove);
      socket.off("suggested-questions", handleSuggestedQuestions);
      socket.off("cutom-goals-triggered", handleCUstomGoalsTriggered);
      socket.off("cooldown-started", handleCooldownStarted);
      socket.off("cooldown-status", handleCooldownStatus);
      socket.off("clear-response-timeout", handleClearResponseTimeout);
    };
  }, [roomId, botData, message]);

  const parseMessage = (htmlMessage: string) => {
    const element = document.createElement("div");
    element.innerHTML = htmlMessage;
    return element.textContent;
  };

  const getActiveHumanAgent = async () => {
    let activeHumanAgent: Record<any, any> = {};
    let humanAgentConfig = botData?.aiBotConfig?.humanAgentConfig || {};
    if (typeof humanAgentConfig == "object") {
      Object.keys(humanAgentConfig).forEach((key) => {
        if (
          typeof humanAgentConfig[key] == "object" &&
          humanAgentConfig[key].isEnable
        ) {
          activeHumanAgent = { ...humanAgentConfig[key], agentType: key };
        }
      });
    }
    return activeHumanAgent;
  };

  // useEffect(() => {
  //   const timer = setTimeout(() => {
  //     setShowLoader(false);
  //   }, 800);
  //   return () => clearTimeout(timer);
  // }, []);

  function mergeData(
    preData: Record<string, any>,
    newData: Record<string, any> = {},
  ) {
    const data = { ...preData };

    for (const key in newData) {
      if (key in preData && !newData[key]) continue;
      data[key] = newData[key];
    }

    return data;
  }

  // useEffect(() => {
  //   if (!botCustomId) return;
  //   if (!socket) return;
  //   if (!socket.connected) {
  //     socket.connect();
  //   }
  //   // Join the bot-specific room
  //   socket.emit("join", { roomId: `bot_${botCustomId}` });

  //   // Listen for bot-deleted event
  //   const handleBotDeleted = (data) => {
  //     if (data.botCustomId === botCustomId) {
  //       // Stop chat and redirect to 404
  //       setDisableInput(true);
  //       setTimeout(() => {
  //         router("/error");
  //       }, 100);
  //     }
  //   };
  //   socket.on("bot-deleted", handleBotDeleted);
  //   return () => {
  //     socket.off("bot-deleted", handleBotDeleted);
  //   };
  // }, [botCustomId]);
  const messageBoxBg = chatConfig?.messageBoxBgColor;
  if (embed) {
    return (
      <div
        ref={wrapperRef}
        className={`cbb-embed-preview-wrapper preview-container-main embed embed-view-container ${responsive || isMobile ? "cbb-mobile-live" : ""}`}
        style={{
          fontFamily: chatConfig?.typography?.font?.fontFamily,
          background: chatConfig?.chatBgColor,
          overscrollBehavior: "contain",
          touchAction: "pan-y pinch-zoom",
          isolation: "isolate",
          contain: "layout style paint",
        }}
      >
        <div
          className={`cbb-embed-header embed-header !gap-[10px]`}
          style={{
            background: chatConfig?.greetingConfig?.headerFooterBgColor,
          }}
        >
          {(showBackBtn || isPreviewModeChat) && (
            <svg
              className="cbb-embed-back-btn cp flex-shrink-0"
              style={{
                cursor: "pointer",
              }}
              onClick={() => props.onBack()}
              xmlns="http://www.w3.org/2000/svg"
              width="9"
              height="16"
              viewBox="0 0 9 16"
              fill="none"
            >
              <path
                d="M8.37616 0.517379C8.48069 0.621058 8.56366 0.744406 8.62028 0.880312C8.6769 1.01622 8.70606 1.16199 8.70606 1.30922C8.70606 1.45645 8.6769 1.60222 8.62028 1.73812C8.56366 1.87403 8.48069 1.99738 8.37616 2.10106L3.26823 7.20899C3.1637 7.31267 3.08073 7.43602 3.02411 7.57192C2.96749 7.70783 2.93833 7.8536 2.93833 8.00083C2.93833 8.14806 2.96749 8.29383 3.02411 8.42974C3.08073 8.56564 3.1637 8.68899 3.26823 8.79267L8.37616 13.9006C8.48069 14.0043 8.56366 14.1276 8.62028 14.2635C8.6769 14.3994 8.70605 14.5452 8.70605 14.6924C8.70605 14.8397 8.6769 14.9854 8.62028 15.1213C8.56366 15.2573 8.48069 15.3806 8.37616 15.4843C8.1672 15.692 7.88453 15.8086 7.5899 15.8086C7.29526 15.8086 7.01259 15.692 6.80363 15.4843L1.68455 10.3652C1.05799 9.73786 0.706055 8.88747 0.706055 8.00083C0.706055 7.11419 1.05799 6.2638 1.68455 5.63646L6.80363 0.517379C7.01259 0.309659 7.29526 0.193065 7.5899 0.193065C7.88453 0.193065 8.1672 0.309659 8.37616 0.517379Z"
                fill={getTextColor(
                  chatConfig?.greetingConfig?.headerFooterBgColor,
                )}
              />
            </svg>
          )}
          <div className="cbb-embed-header-container embed-left-item fx al-cnt w-full justify-between gap-[10px] pe-[40px]">
            <div className="cbb-embed-header-content fx items-center gap-[10px]">
              <div
                className={`cbb-embed-header-logo flex-cnt h-[50px] w-[50px] rounded-[100px] p-[0px]`}
              >
                {chatConfig?.logo ? (
                  <img
                    src={chatConfig?.logo}
                    alt="boticon"
                    className="cbb-embed-header-logo-image !h-[50px] !w-[50px] !rounded-[100px]"
                  />
                ) : fromOutgrow ? (
                  <OutgrowBotIcon />
                ) : (
                  <BotIconOld />
                )}
              </div>
              <div className="cbb-embed-header-inner embed-header-inner grid gap-[5px]">
                <h3
                  className={`cbb-embed-header-title !font-medium`}
                  style={{
                    fontSize:
                      chatConfig?.greetingConfig?.header?.fontSize + "px",
                    color: getTextColor(
                      chatConfig?.greetingConfig?.headerFooterBgColor,
                    ),
                  }}
                >
                  {chatConfig?.greetingConfig?.header?.text.slice(0, 30)}
                </h3>
                {chatConfig?.greetingConfig?.description?.enabled && (
                  <p
                    className={`cbb-embed-header-description`}
                    style={{
                      fontSize:
                        chatConfig?.greetingConfig?.description?.fontSize +
                        "px",
                      color: getTextColor(
                        chatConfig?.greetingConfig?.headerFooterBgColor,
                      ),
                    }}
                  >
                    {chatConfig?.greetingConfig?.description?.text}
                  </p>
                )}
              </div>
            </div>
            {(isMobileParent ||
              botData?.companyId === "64652848be7f202bf2716ffe") && (
              <button
                className="cbb-mobile-screen-cross-icon grid h-[20px] w-[20px] flex-shrink-0 place-items-center rounded-full"
                style={{
                  background: deriveShade(
                    chatConfig?.greetingConfig?.headerFooterBgColor,
                  ),
                }}
                onClick={() => {
                  window.parent.postMessage({ type: "close" }, "*");
                }}
              >
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M11.8323 10.0166L19.62 2.22871C20.1268 1.72214 20.1268 0.90305 19.62 0.396534C19.1134 -0.110043 18.2943 -0.110043 17.7878 0.396534L9.99991 8.18433L2.21229 0.396534C1.70547 -0.110043 0.886691 -0.110043 0.380115 0.396534C-0.126705 0.903111 -0.126705 1.72214 0.380115 2.22871L8.16767 10.0166L0.380175 17.8044C-0.126644 18.3109 -0.126644 19.13 0.380175 19.6365C0.500338 19.7571 0.643127 19.8526 0.80034 19.9178C0.957552 19.9829 1.12609 20.0164 1.29627 20.0162C1.62791 20.0162 1.95967 19.8892 2.21236 19.6365L9.99991 11.8488L17.7878 19.6365C17.908 19.757 18.0508 19.8526 18.208 19.9177C18.3652 19.9829 18.5337 20.0163 18.7039 20.0162C19.0356 20.0162 19.3673 19.8892 19.62 19.6365C20.1268 19.13 20.1268 18.3109 19.62 17.8044L11.8323 10.0166Z"
                    fill={getTextColor(
                      deriveShade(
                        chatConfig?.greetingConfig?.headerFooterBgColor,
                      ),
                    )}
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
        <div
          className={`cbb-embed-preview-container previewContainer new-scrollbar embed embed-scroll relative`}
          ref={scrollToButtomRef}
          // style={{ background: chatConfig?.chatBgColor }}
        >
          <div className="cbb-embed-chats-wrapper centered-div embed">
            {/* {showLoader ? (
              <span
                className="cbb-embed-chats-loader before-chat-loader"
                style={{
                  borderTop:
                    chatConfig?.theme === "dark"
                      ? "3px solid white"
                      : "3px solid black",
                }}
              ></span>
            ) : ( */}
            <>
              <div className="cbb-embed-chats-container chatDataContainer">
                {message?.map((msg, currentIndex) => {
                  if (msg.name === "button") {
                    return (
                      <Fragment key={currentIndex}>
                        {msg.author === 0 ? (
                          <BotComponent
                            reasoning={reasoning}
                            hideClickableOptions={
                              suggestedQuestionsData?.length > 0
                            }
                            ref={botComponentRef}
                            responseCacheOptions={responseCacheOptions}
                            toolLoader={
                              message.length - 1 === currentIndex
                                ? usingToolLoader
                                : false
                            }
                            currentIndex={currentIndex}
                            chatConfig={chatConfig}
                            msg={msg}
                            embed={embed}
                            botData={botData}
                            starterQues={starterQues}
                            activeCat={activeCat}
                            handleStarterQues={handleStarterQues}
                            textColor={textColor}
                            suggestedQuesAns={suggestedQuesAns}
                            message={message}
                            setReadPdf={setReadPdf}
                            setReadPdfText={setReadPdfText}
                            handleReaction={handleReaction}
                            dataSource={dataSource}
                            userCredential={userCredential}
                            calendlyConfig={calendlyConfig}
                            handleUserReply={handleUserReply}
                            isAnswerSaved={isAnswerSaved}
                            handleRegenerate={handleRegenerate}
                            embedConfig={botData?.embedConfig}
                            previousChat={props.resumeConversationData}
                            customGoalsMsg={customGoalsMsg.current}
                            steps={steps.current}
                            setDisableInput={setDisableInput}
                            isLast={islast}
                            isOverLimit={overLimit}
                          />
                        ) : (
                          <UserComponent
                            isCurrentLastUserMessage={
                              lastUserMsgIndex === currentIndex
                            }
                            currentIndex={currentIndex}
                            chatConfig={chatConfig}
                            msg={msg}
                            textColor={textColor}
                            embed={embed}
                            previousChat={props.resumeConversationData}
                          />
                        )}
                        {!msg.hasAnswered && (
                          <div className="cbb-button-container buttonContainer embed">
                            {msg.options
                              .sort(
                                (a, b) =>
                                  (a.image?.url.length || 0) -
                                  (b.image?.url.length || 0),
                              )
                              .map((value, index) => (
                                <div
                                  className="cbb-question-button questionButton flex-cnt fx-d-col gap-10"
                                  key={index}
                                  onClick={() => {
                                    handleButtonReply(
                                      currentIndex,
                                      value.name,
                                      index,
                                      value.value,
                                    );
                                  }}
                                >
                                  {msg?.showImg && value.image && (
                                    <img src={value.image.url} alt="" />
                                  )}
                                  <div className="cbb-option-title-wrapper option-title-wrapper flex-cnt fx-strt wd-100 gap-10">
                                    <div className="cbb-circle-radio-outer circle-radio-outer">
                                      <div className="cbb-circle-radio-inner circle-radio-inner"></div>
                                    </div>
                                    <p
                                      style={{
                                        fontSize:
                                          chatConfig?.typography?.size + "px",
                                      }}
                                    >
                                      {value.name}
                                    </p>
                                  </div>
                                </div>
                              ))}
                          </div>
                        )}
                      </Fragment>
                    );
                  } else {
                    return (
                      <Fragment key={currentIndex}>
                        {msg.author === 0 ? (
                          <BotComponent
                            reasoning={reasoning}
                            hideClickableOptions={
                              suggestedQuestionsData?.length > 0
                            }
                            ref={botComponentRef}
                            responseCacheOptions={responseCacheOptions}
                            toolLoader={
                              message.length - 1 === currentIndex
                                ? usingToolLoader
                                : false
                            }
                            currentIndex={currentIndex}
                            chatConfig={chatConfig}
                            msg={msg}
                            botType={botType}
                            botData={botData}
                            starterQues={starterQues}
                            activeCat={activeCat}
                            handleStarterQues={handleStarterQues}
                            textColor={textColor}
                            suggestedQuesAns={suggestedQuesAns}
                            message={message}
                            setReadPdf={setReadPdf}
                            setReadPdfText={setReadPdfText}
                            dataSource={dataSource}
                            handleReaction={handleReaction}
                            userCredential={userCredential}
                            calendlyConfig={calendlyConfig}
                            handleUserReply={handleUserReply}
                            isAnswerSaved={isAnswerSaved}
                            handleRegenerate={handleRegenerate}
                            embedConfig={botData?.embedConfig}
                            embed={embed}
                            responseLoader={responseLoader}
                            previousChat={props.resumeConversationData}
                            customGoalsMsg={customGoalsMsg.current}
                            steps={steps.current}
                            setDisableInput={setDisableInput}
                            isLast={islast}
                            isOverLimit={overLimit}
                          />
                        ) : (
                          <UserComponent
                            isCurrentLastUserMessage={
                              lastUserMsgIndex === currentIndex
                            }
                            currentIndex={currentIndex}
                            chatConfig={chatConfig}
                            msg={msg}
                            textColor={textColor}
                            embed={embed}
                            botType={botType}
                            previousChat={props.resumeConversationData}
                          />
                        )}
                      </Fragment>
                    );
                  }
                })}
              </div>
              <span ref={scrollRef}></span>
            </>
            {/* )} */}
          </div>

          {showGoToBottom && !responseLoader && (
            <div
              className={`cbb-got-to-bottom-button gotobottom-btn fx sticky bottom-[20px] left-1/2 z-10 h-[36px] w-[36px] -translate-x-1/2 cursor-pointer items-center justify-center rounded-[100px] ${
                chatConfig?.theme === "dark"
                  ? "bg-[#6D6D6D]"
                  : "bg-[#fff] drop-shadow-[0_4px_10px_rgba(0,0,0,0.15)]"
              }`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                scrollToBottom(e);
              }}
            >
              <GoToBottomIcon
                fill={chatConfig?.theme === "dark" ? "#fff" : "#444"}
              />
            </div>
          )}
        </div>
        <div className={`cbb-chat-screen-footer footer-sec !bg-transparent`}>
          {/* {botData?.enableGemini && <VoiceCoach url={undefined} botCustomId={botData?.botCustomId} />} */}
          {isAiResError && !isCustomProviderError && (
            <div
              className={`cbb-error-msg-wrapper fx py-[10px m-[20px] items-center justify-between gap-[20px] rounded-[15px] px-[16px]`}
              style={{ background: messageBoxBg }}
            >
              <div className="cbb-error-msg-content al-cnt grid grid-flow-col gap-[6px]">
                <svg
                  width="17"
                  height="16"
                  viewBox="0 0 17 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-[16px] w-[16px]`}
                >
                  <g clipPath="url(#clip0_3826_34009)">
                    <path
                      d="M8.60743 0C7.02518 0 5.47846 0.469192 4.16286 1.34824C2.84727 2.22729 1.82189 3.47672 1.21639 4.93853C0.610888 6.40034 0.452461 8.00887 0.761143 9.56072C1.06982 11.1126 1.83175 12.538 2.95057 13.6569C4.06939 14.7757 5.49486 15.5376 7.0467 15.8463C8.59855 16.155 10.2071 15.9965 11.6689 15.391C13.1307 14.7855 14.3801 13.7602 15.2592 12.4446C16.1382 11.129 16.6074 9.58225 16.6074 8C16.6051 5.87897 15.7615 3.84547 14.2617 2.34568C12.762 0.845886 10.7285 0.00229405 8.60743 0ZM8.60743 14.6667C7.28888 14.6667 5.99995 14.2757 4.90362 13.5431C3.8073 12.8106 2.95281 11.7694 2.44823 10.5512C1.94364 9.33305 1.81162 7.99261 2.06886 6.6994C2.32609 5.40619 2.96103 4.21831 3.89338 3.28596C4.82573 2.35361 6.01362 1.71867 7.30682 1.46143C8.60003 1.2042 9.94048 1.33622 11.1586 1.8408C12.3768 2.34539 13.418 3.19987 14.1506 4.2962C14.8831 5.39253 15.2741 6.68146 15.2741 8C15.2722 9.76752 14.5691 11.4621 13.3193 12.7119C12.0695 13.9617 10.3749 14.6647 8.60743 14.6667Z"
                      fill={getTextColor(messageBoxBg)}
                    />
                    <path
                      d="M8.60775 6.66797H7.94108C7.76427 6.66797 7.5947 6.73821 7.46968 6.86323C7.34465 6.98826 7.27441 7.15782 7.27441 7.33464C7.27441 7.51145 7.34465 7.68102 7.46968 7.80604C7.5947 7.93106 7.76427 8.0013 7.94108 8.0013H8.60775V12.0013C8.60775 12.1781 8.67799 12.3477 8.80301 12.4727C8.92803 12.5977 9.0976 12.668 9.27441 12.668C9.45123 12.668 9.62079 12.5977 9.74582 12.4727C9.87084 12.3477 9.94108 12.1781 9.94108 12.0013V8.0013C9.94108 7.64768 9.8006 7.30854 9.55056 7.05849C9.30051 6.80844 8.96137 6.66797 8.60775 6.66797Z"
                      fill={getTextColor(messageBoxBg)}
                    />
                    <path
                      d="M8.60742 5.33203C9.15971 5.33203 9.60742 4.88432 9.60742 4.33203C9.60742 3.77975 9.15971 3.33203 8.60742 3.33203C8.05514 3.33203 7.60742 3.77975 7.60742 4.33203C7.60742 4.88432 8.05514 5.33203 8.60742 5.33203Z"
                      fill={getTextColor(messageBoxBg)}
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_3826_34009">
                      <rect
                        width="16"
                        height="16"
                        fill="white"
                        transform="translate(0.607422)"
                      />
                    </clipPath>
                  </defs>
                </svg>
                <p
                  className={`cbb-error-text py-[5px] text-[12px]`}
                  style={{
                    color: getTextColor(messageBoxBg),
                  }}
                >
                  {ERROR_CONNECTION_ISSUE[
                    botData?.aiBotConfig?.language?.toLowerCase()
                  ] || ERROR_CONNECTION_ISSUE.english}
                </p>
              </div>
              <button
                style={{
                  background: getTextColor(messageBoxBg),
                  color: messageBoxBg,
                }}
                className={`cbb-retry-button fx items-center gap-[6px] whitespace-nowrap rounded-[100px] px-[12px] py-[6px] text-[12px]`}
                onClick={() => handleRegenerate(true)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="13"
                  height="12"
                  viewBox="0 0 13 12"
                  fill="none"
                >
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M6.60742 2.0005C5.31642 2.0005 3.99392 2.619 3.05842 3.75H4.10742C4.24003 3.75 4.36721 3.80268 4.46098 3.89645C4.55474 3.99021 4.60742 4.11739 4.60742 4.25C4.60742 4.38261 4.55474 4.50979 4.46098 4.60355C4.36721 4.69732 4.24003 4.75 4.10742 4.75H2.10742C1.97481 4.75 1.84764 4.69732 1.75387 4.60355C1.6601 4.50979 1.60742 4.38261 1.60742 4.25L1.60742 2C1.60742 1.86739 1.6601 1.74021 1.75387 1.64645C1.84764 1.55268 1.97481 1.5 2.10742 1.5C2.24003 1.5 2.36721 1.55268 2.46098 1.64645C2.55474 1.74021 2.60742 1.86739 2.60742 2L2.60742 2.756C3.69742 1.631 5.14842 1 6.60742 1C9.36842 1 11.6064 3.2385 11.6064 6C11.6064 8.761 9.36792 11 6.60742 11C5.68969 11.0002 4.78961 10.7478 4.00582 10.2704C3.22203 9.79299 2.58479 9.10904 2.16392 8.2935C2.13236 8.23507 2.11275 8.17094 2.10624 8.10485C2.09974 8.03876 2.10646 7.97204 2.12602 7.90858C2.14558 7.84511 2.17759 7.78619 2.22018 7.73523C2.26276 7.68427 2.31507 7.64231 2.37405 7.61179C2.43303 7.58127 2.4975 7.5628 2.5637 7.55747C2.62989 7.55214 2.69648 7.56005 2.75959 7.58074C2.82269 7.60142 2.88104 7.63447 2.93124 7.67796C2.98143 7.72144 3.02246 7.77449 3.05192 7.834C3.37746 8.46519 3.86517 8.99833 4.46495 9.37864C5.06473 9.75895 5.75495 9.97271 6.4647 9.99797C7.17444 10.0232 7.87811 9.85907 8.50341 9.52236C9.12871 9.18564 9.65309 8.68852 10.0227 8.08207C10.3922 7.47562 10.5937 6.7817 10.6063 6.07162C10.619 5.36154 10.4423 4.6609 10.0945 4.04169C9.74674 3.42248 9.24037 2.90703 8.62743 2.5483C8.0145 2.18957 7.31711 2.00049 6.60692 2.0005H6.60742Z"
                    fill={messageBoxBg}
                  />
                </svg>
                {RETRY_LABEL[botData?.aiBotConfig?.language?.toLowerCase()] ||
                  RETRY_LABEL.english}
              </button>
            </div>
          )}

          {isCustomProviderError && (
            <div
              className={`cbb-error-msg-wrapper fx m-[20px] items-center justify-between gap-[20px] rounded-[15px] px-[16px] py-[10px]`}
              style={{ background: messageBoxBg }}
            >
              <div className="cbb-error-msg-content al-cnt grid grid-flow-col gap-[6px]">
                <svg
                  width="17"
                  height="16"
                  viewBox="0 0 17 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-[16px] w-[16px]`}
                >
                  <g clipPath="url(#clip0_3826_34009)">
                    <path
                      d="M8.60743 0C7.02518 0 5.47846 0.469192 4.16286 1.34824C2.84727 2.22729 1.82189 3.47672 1.21639 4.93853C0.610888 6.40034 0.452461 8.00887 0.761143 9.56072C1.06982 11.1126 1.83175 12.538 2.95057 13.6569C4.06939 14.7757 5.49486 15.5376 7.0467 15.8463C8.59855 16.155 10.2071 15.9965 11.6689 15.391C13.1307 14.7855 14.3801 13.7602 15.2592 12.4446C16.1382 11.129 16.6074 9.58225 16.6074 8C16.6051 5.87897 15.7615 3.84547 14.2617 2.34568C12.762 0.845886 10.7285 0.00229405 8.60743 0ZM8.60743 14.6667C7.28888 14.6667 5.99995 14.2757 4.90362 13.5431C3.8073 12.8106 2.95281 11.7694 2.44823 10.5512C1.94364 9.33305 1.81162 7.99261 2.06886 6.6994C2.32609 5.40619 2.96103 4.21831 3.89338 3.28596C4.82573 2.35361 6.01362 1.71867 7.30682 1.46143C8.60003 1.2042 9.94048 1.33622 11.1586 1.8408C12.3768 2.34539 13.418 3.19987 14.1506 4.2962C14.8831 5.39253 15.2741 6.68146 15.2741 8C15.2722 9.76752 14.5691 11.4621 13.3193 12.7119C12.0695 13.9617 10.3749 14.6647 8.60743 14.6667Z"
                      fill={getTextColor(messageBoxBg)}
                    />
                    <path
                      d="M8.60775 6.66797H7.94108C7.76427 6.66797 7.5947 6.73821 7.46968 6.86323C7.34465 6.98826 7.27441 7.15782 7.27441 7.33464C7.27441 7.51145 7.34465 7.68102 7.46968 7.80604C7.5947 7.93106 7.76427 8.0013 7.94108 8.0013H8.60775V12.0013C8.60775 12.1781 8.67799 12.3477 8.80301 12.4727C8.92803 12.5977 9.0976 12.668 9.27441 12.668C9.45123 12.668 9.62079 12.5977 9.74582 12.4727C9.87084 12.3477 9.94108 12.1781 9.94108 12.0013V8.0013C9.94108 7.64768 9.8006 7.30854 9.55056 7.05849C9.30051 6.80844 8.96137 6.66797 8.60775 6.66797Z"
                      fill={getTextColor(messageBoxBg)}
                    />
                    <path
                      d="M8.60742 5.33203C9.15971 5.33203 9.60742 4.88432 9.60742 4.33203C9.60742 3.77975 9.15971 3.33203 8.60742 3.33203C8.05514 3.33203 7.60742 3.77975 7.60742 4.33203C7.60742 4.88432 8.05514 5.33203 8.60742 5.33203Z"
                      fill={getTextColor(messageBoxBg)}
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_3826_34009">
                      <rect
                        width="16"
                        height="16"
                        fill="white"
                        transform="translate(0.607422)"
                      />
                    </clipPath>
                  </defs>
                </svg>
                <p
                  className={`cbb-error-text text-[12px]`}
                  style={{
                    color: getTextColor(messageBoxBg),
                  }}
                >
                  {cooldownMessage}
                </p>
              </div>
              <button
                style={{
                  background: getTextColor(messageBoxBg),
                  color: messageBoxBg,
                }}
                className={`cbb-retry-button fx items-center gap-[6px] whitespace-nowrap rounded-[100px] px-[12px] py-[6px] text-[12px]`}
                onClick={() => {
                  handleRegenerate(true);
                  setIsCustomProviderError(false);
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="13"
                  height="12"
                  viewBox="0 0 13 12"
                  fill="none"
                  className={``}
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M6.60742 2.0005C5.31642 2.0005 3.99392 2.619 3.05842 3.75H4.10742C4.24003 3.75 4.36721 3.80268 4.46098 3.89645C4.55474 3.99021 4.60742 4.11739 4.60742 4.25C4.60742 4.38261 4.55474 4.50979 4.46098 4.60355C4.36721 4.69732 4.24003 4.75 4.10742 4.75H2.10742C1.97481 4.75 1.84764 4.69732 1.75387 4.60355C1.6601 4.50979 1.60742 4.38261 1.60742 4.25L1.60742 2C1.60742 1.86739 1.6601 1.74021 1.75387 1.64645C1.84764 1.55268 1.97481 1.5 2.10742 1.5C2.24003 1.5 2.36721 1.55268 2.46098 1.64645C2.55474 1.74021 2.60742 1.86739 2.60742 2L2.60742 2.756C3.69742 1.631 5.14842 1 6.60742 1C9.36842 1 11.6064 3.2385 11.6064 6C11.6064 8.761 9.36792 11 6.60742 11C5.68969 11.0002 4.78961 10.7478 4.00582 10.2704C3.22203 9.79299 2.58479 9.10904 2.16392 8.2935C2.13236 8.23507 2.11275 8.17094 2.10624 8.10485C2.09974 8.03876 2.10646 7.97204 2.12602 7.90858C2.14558 7.84511 2.17759 7.78619 2.22018 7.73523C2.26276 7.68427 2.31507 7.64231 2.37405 7.61179C2.43303 7.58127 2.4975 7.5628 2.5637 7.55747C2.62989 7.55214 2.69648 7.56005 2.75959 7.58074C2.82269 7.60142 2.88104 7.63447 2.93124 7.67796C2.98143 7.72144 3.02246 7.77449 3.05192 7.834C3.37746 8.46519 3.86517 8.99833 4.46495 9.37864C5.06473 9.75895 5.75495 9.97271 6.4647 9.99797C7.17444 10.0232 7.87811 9.85907 8.50341 9.52236C9.12871 9.18564 9.65309 8.68852 10.0227 8.08207C10.3922 7.47562 10.5937 6.7817 10.6063 6.07162C10.619 5.36154 10.4423 4.6609 10.0945 4.04169C9.74674 3.42248 9.24037 2.90703 8.62743 2.5483C8.0145 2.18957 7.31711 2.00049 6.60692 2.0005H6.60742Z"
                    fill={messageBoxBg}
                  />
                </svg>
                {RETRY_LABEL[botData?.aiBotConfig?.language?.toLowerCase()] ||
                  RETRY_LABEL.english}
              </button>
            </div>
          )}

          {isCustomProviderError && !cooldownActive && (
            <div
              className={`cbb-custom-provider-error fx m-[20px] items-center justify-between gap-[20px] rounded-[15px] px-[16px] py-[10px]`}
              style={{ background: messageBoxBg }}
            >
              <div className="cbb-error-msg-content al-cnt grid grid-flow-col gap-[6px]">
                <svg
                  width="17"
                  height="16"
                  viewBox="0 0 17 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-[16px] w-[16px]`}
                >
                  <g clipPath="url(#clip0_3826_34009)">
                    <path
                      d="M8.60743 0C7.02518 0 5.47846 0.469192 4.16286 1.34824C2.84727 2.22729 1.82189 3.47672 1.21639 4.93853C0.610888 6.40034 0.452461 8.00887 0.761143 9.56072C1.06982 11.1126 1.83175 12.538 2.95057 13.6569C4.06939 14.7757 5.49486 15.5376 7.0467 15.8463C8.59855 16.155 10.2071 15.9965 11.6689 15.391C13.1307 14.7855 14.3801 13.7602 15.2592 12.4446C16.1382 11.129 16.6074 9.58225 16.6074 8C16.6051 5.87897 15.7615 3.84547 14.2617 2.34568C12.762 0.845886 10.7285 0.00229405 8.60743 0ZM8.60743 14.6667C7.28888 14.6667 5.99995 14.2757 4.90362 13.5431C3.8073 12.8106 2.95281 11.7694 2.44823 10.5512C1.94364 9.33305 1.81162 7.99261 2.06886 6.6994C2.32609 5.40619 2.96103 4.21831 3.89338 3.28596C4.82573 2.35361 6.01362 1.71867 7.30682 1.46143C8.60003 1.2042 9.94048 1.33622 11.1586 1.8408C12.3768 2.34539 13.418 3.19987 14.1506 4.2962C14.8831 5.39253 15.2741 6.68146 15.2741 8C15.2722 9.76752 14.5691 11.4621 13.3193 12.7119C12.0695 13.9617 10.3749 14.6647 8.60743 14.6667Z"
                      fill={getTextColor(messageBoxBg)}
                    />
                    <path
                      d="M8.60775 6.66797H7.94108C7.76427 6.66797 7.5947 6.73821 7.46968 6.86323C7.34465 6.98826 7.27441 7.15782 7.27441 7.33464C7.27441 7.51145 7.34465 7.68102 7.46968 7.80604C7.5947 7.93106 7.76427 8.0013 7.94108 8.0013H8.60775V12.0013C8.60775 12.1781 8.67799 12.3477 8.80301 12.4727C8.92803 12.5977 9.0976 12.668 9.27441 12.668C9.45123 12.668 9.62079 12.5977 9.74582 12.4727C9.87084 12.3477 9.94108 12.1781 9.94108 12.0013V8.0013C9.94108 7.64768 9.8006 7.30854 9.55056 7.05849C9.30051 6.80844 8.96137 6.66797 8.60775 6.66797Z"
                      fill={getTextColor(messageBoxBg)}
                    />
                    <path
                      d="M8.60742 5.33203C9.15971 5.33203 9.60742 4.88432 9.60742 4.33203C9.60742 3.77975 9.15971 3.33203 8.60742 3.33203C8.05514 3.33203 7.60742 3.77975 7.60742 4.33203C7.60742 4.88432 8.05514 5.33203 8.60742 5.33203Z"
                      fill={getTextColor(messageBoxBg)}
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_3826_34009">
                      <rect
                        width="16"
                        height="16"
                        fill="white"
                        transform="translate(0.607422)"
                      />
                    </clipPath>
                  </defs>
                </svg>
                <p
                  className={`text-[12px]`}
                  style={{
                    color: getTextColor(messageBoxBg),
                  }}
                >
                  {ERROR_CONNECTION_ISSUE[
                    botData?.aiBotConfig?.language?.toLowerCase()
                  ] || ERROR_CONNECTION_ISSUE.english}
                </p>
              </div>
              <button
                style={{
                  background: getTextColor(messageBoxBg),
                  color: messageBoxBg,
                }}
                className={`cbb-retry-button fx items-center gap-[6px] whitespace-nowrap rounded-[100px] px-[12px] py-[6px] text-[12px]`}
                onClick={() => {
                  handleRegenerate(true);
                  setIsCustomProviderError(false);
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="13"
                  height="12"
                  viewBox="0 0 13 12"
                  fill="none"
                  className={``}
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M6.60742 2.0005C5.31642 2.0005 3.99392 2.619 3.05842 3.75H4.10742C4.24003 3.75 4.36721 3.80268 4.46098 3.89645C4.55474 3.99021 4.60742 4.11739 4.60742 4.25C4.60742 4.38261 4.55474 4.50979 4.46098 4.60355C4.36721 4.69732 4.24003 4.75 4.10742 4.75H2.10742C1.97481 4.75 1.84764 4.69732 1.75387 4.60355C1.6601 4.50979 1.60742 4.38261 1.60742 4.25L1.60742 2C1.60742 1.86739 1.6601 1.74021 1.75387 1.64645C1.84764 1.55268 1.97481 1.5 2.10742 1.5C2.24003 1.5 2.36721 1.55268 2.46098 1.64645C2.55474 1.74021 2.60742 1.86739 2.60742 2L2.60742 2.756C3.69742 1.631 5.14842 1 6.60742 1C9.36842 1 11.6064 3.2385 11.6064 6C11.6064 8.761 9.36792 11 6.60742 11C5.68969 11.0002 4.78961 10.7478 4.00582 10.2704C3.22203 9.79299 2.58479 9.10904 2.16392 8.2935C2.13236 8.23507 2.11275 8.17094 2.10624 8.10485C2.09974 8.03876 2.10646 7.97204 2.12602 7.90858C2.14558 7.84511 2.17759 7.78619 2.22018 7.73523C2.26276 7.68427 2.31507 7.64231 2.37405 7.61179C2.43303 7.58127 2.4975 7.5628 2.5637 7.55747C2.62989 7.55214 2.69648 7.56005 2.75959 7.58074C2.82269 7.60142 2.88104 7.63447 2.93124 7.67796C2.98143 7.72144 3.02246 7.77449 3.05192 7.834C3.37746 8.46519 3.86517 8.99833 4.46495 9.37864C5.06473 9.75895 5.75495 9.97271 6.4647 9.99797C7.17444 10.0232 7.87811 9.85907 8.50341 9.52236C9.12871 9.18564 9.65309 8.68852 10.0227 8.08207C10.3922 7.47562 10.5937 6.7817 10.6063 6.07162C10.619 5.36154 10.4423 4.6609 10.0945 4.04169C9.74674 3.42248 9.24037 2.90703 8.62743 2.5483C8.0145 2.18957 7.31711 2.00049 6.60692 2.0005H6.60742Z"
                    fill={messageBoxBg}
                  />
                </svg>
                Retry
              </button>
            </div>
          )}
          <div className="cbb-chat-bottom branding">
            {suggestedQuestionsData.length > 0 && (
              <div className="cbb-suggested-questions suggested-que relative mx-auto w-[50%]">
                {/* <h6
                  style={{
                  }}
                  className={`text-text-clr text-[14px] font-medium ${chatConfig?.theme === "dark" ? "text-[#fff]" : "text-text-clr"}`}
                >
                  Suggestions
                </h6> */}
                <Swiper
                  modules={[Navigation, Pagination, Autoplay]}
                  spaceBetween={10}
                  slidesPerView="auto"
                  navigation={false}
                  pagination={false}
                  loop={false}
                  speed={600}
                  className="suggested-questions-swiper"
                  style={{
                    paddingInlineStart: "10px",
                    paddingInlineEnd: "10px",
                    paddingTop: `10px`,
                  }}
                  onSlideChange={(swiper) => {
                    if (swiper.isEnd) {
                      swiper.autoplay.stop();
                    }
                  }}
                >
                  {suggestedQuestionsData.map((item, index) => (
                    <SwiperSlide
                      className="cbb-suggested-questions-slide"
                      key={index}
                      style={{
                        width: "auto",
                        height: "auto",
                      }}
                    >
                      <motion.div
                        initial={{ opacity: 0, x: 20, scale: 0.2 }}
                        animate={{
                          opacity: 1,
                          x: 0,
                          scale: 1,
                          transition: {
                            duration: 0.4,
                            ease: "easeOut",
                            delay: index * 0.1,
                          },
                        }}
                        whileHover={{
                          scale: 1.02,
                          y: -1,
                          transition: { duration: 0.2 },
                        }}
                        whileTap={{ scale: 0.98 }}
                        className="cbb-suggested-question cursor-pointer whitespace-nowrap text-nowrap rounded-[100px] border px-[15px] py-[10px]"
                        onClick={() => {
                          const lastIndex = message.length
                            ? message.length - 1
                            : 0;
                          handleUserReply(lastIndex, false, item, false);
                        }}
                        style={{
                          fontSize: chatConfig?.typography?.size + "px",
                          border: `1px solid ${getTextColor(chatConfig?.chatBgColor)}`,
                          color: getTextColor(chatConfig?.chatBgColor),
                        }}
                      >
                        {item}
                      </motion.div>
                    </SwiperSlide>
                  ))}
                </Swiper>
                <div
                  style={{
                    background: `linear-gradient(90deg, ${getColorWithAlpha(chatConfig?.chatBgColor, 0)} 0%, ${chatConfig?.chatBgColor} 66.82%)`,
                  }}
                  className={`cbb-fadeout-div fadeout-div !right-[0] !top-0 !h-full !w-[20px]`}
                ></div>
              </div>
            )}
            <div
              style={{
                opacity: disableInput || islast || overLimit ? "0.3" : "1",
                borderRadius: `${borderRadius}px`,
                background: messageBoxBg,
              }}
              className={`cbb-user-container-preview user-container-preview embed rounded-[100px]`}
            >
              <textarea
                className={`cbb-user-textarea userInput embed_tarea new-scrollbar my-[5px] me-[8px] !bg-transparent !pb-[5px] !pt-[8px]`}
                ref={textareaRef}
                style={{
                  color: getTextColor(messageBoxBg),
                }}
                placeholder={
                  COMPOSE_MESSAGE_PLACEHOLDER[
                    botData?.aiBotConfig?.language?.toLowerCase()
                  ] || COMPOSE_MESSAGE_PLACEHOLDER.english + "..."
                }
                autoFocus={!isMobile}
                value={newReply}
                onChange={handleButtomInput}
                onKeyDown={handleSendViaEnter}
                readOnly={disableInput || islast || overLimit || !editable}
                onFocus={() => setEditable(true)}
                onTouchStart={() => {
                  if (!(disableInput || islast || overLimit)) {
                    setEditable(true);
                    setTimeout(() => textareaRef.current?.focus(), 100);
                  }
                }}
              />
              <div
                className={`cbb-suggested-cta suggested-cta flex-cnt ${
                  !botData?.aiStarterQues?.data.length ? "none" : ""
                } ${
                  message[message.length - 1]?.text?.isMandatory
                    ? "opacity-30"
                    : ""
                }`}
              >
                <div
                  className="cbb-suggested-cta-content"
                  onClick={() => {
                    if (message[message.length - 1]?.isLoader) return;
                    setSuggestedQuesAns(true);
                    setStarterQues(botData?.aiStarterQues?.data);
                  }}
                >
                  <SuggestedQuesAns />
                </div>
              </div>
              <div className="cbb-userBtn userBtn"></div>
              <div className="cbb-embed-userBtn userBtn embed__send">
                <div
                  className="cbb-send-button-wrapper svg-box m-right-8 d !rounded-[100px]"
                  style={{
                    background: `${
                      botData?.url === "omni001-290" ||
                      botData?.url === "tarun-4" ||
                      botData?.url === "tarun-25"
                        ? "black"
                        : chatConfig?.color === "#F1F1F1"
                          ? ""
                          : chatConfig?.color
                    }`,
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    responseLoader ? handleStop() : handleSendReplyButton();
                  }}
                >
                  <span
                    className="cbb-send-button"
                    style={{
                      color: `${
                        chatConfig?.color === "#F1F1F1" ? "#fff" : textColor
                      }`,
                    }}
                  >
                    {responseLoader ? (
                      <svg
                        className="cbb-stop-generation-svg"
                        width="15"
                        height="15"
                        viewBox="0 0 15 15"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect
                          x="0.607422"
                          y="0.5"
                          width="14"
                          height="14"
                          rx="4"
                          fill={chatConfig?.typography?.color}
                        />
                      </svg>
                    ) : (
                      <svg
                        className="cbb-send-svg"
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="17"
                        viewBox="0 0 16 17"
                        fill="none"
                      >
                        <path
                          d="M1.12457 0.888736L15.5045 8.07234C15.7365 8.18823 15.8318 8.47283 15.7175 8.70801C15.6719 8.80185 15.597 8.87778 15.5045 8.92402L1.12487 16.1075C0.892888 16.2234 0.612165 16.1267 0.497855 15.8915C0.447871 15.7886 0.436284 15.6709 0.465235 15.5601L1.88993 10.1057C1.93705 9.92536 2.08403 9.78969 2.26558 9.759L8.70978 8.66968C8.78873 8.65634 8.85401 8.6035 8.88538 8.53167L8.90224 8.47457C8.92046 8.36373 8.85976 8.25804 8.76172 8.21654L8.70978 8.20141L2.22919 7.10639C2.04757 7.0757 1.90054 6.93995 1.85347 6.75949L0.46491 1.436C0.398763 1.18253 0.547815 0.922697 0.797827 0.855636C0.907113 0.826322 1.02317 0.838079 1.12457 0.888736Z"
                          fill={
                            botData?.url === "omni001-290" ||
                            botData?.url === "tarun-4" ||
                            botData?.url === "tarun-25"
                              ? "white"
                              : chatConfig?.typography?.color
                          }
                        />
                      </svg>
                    )}
                  </span>
                </div>
              </div>
            </div>

            {!isReplyValid && showError && error !== "Field is required" && (
              <span className="cbb-small-error-txt small-txt error p-left no-m-tb">
                {error}
              </span>
            )}
            {checkValidate && (
              <span className="cbb-small-error-txt small-txt error p-left no-m-tb">
                {error}
              </span>
            )}
          </div>
          {botData?.cookieConsent && consent && (
            <div
              className={`cbb-cookie-consent cookieConsent cookie-popup h-max ${chatConfig?.omniBranding ? "!mb-[-10px]" : ""}`}
              style={{
                background: deriveShade(chatConfig?.chatBgColor),
              }}
            >
              <div
                dangerouslySetInnerHTML={{
                  __html: botData?.cookieConsentContent,
                }}
                className={`cbb-cookie-consent-inner cookieConsent-inner bg-transparent`}
                style={{
                  color: getTextColor(deriveShade(chatConfig?.chatBgColor)),
                }}
              />
              <div
                className={`cbb-cookie-btn-wrapper cbb-cookie-btn-wqr btn btn-popup bg-transparent`}
              >
                <button
                  onClick={() => {
                    setConsent(false);
                    window.localStorage.setItem("privacy_policy", "true");
                    const expires = new Date();
                    expires.setTime(
                      expires.getTime() + 365 * 24 * 60 * 60 * 1000,
                    );
                    document.cookie = `privacy_policy=true; expires=${expires.toUTCString()}; path=/`;

                    // Send message to parent window to load GTM
                    try {
                      window.parent.postMessage(
                        {
                          type: "LOAD_GTM",
                          gtmId: "GTM-PS96TMC8",
                          consent: true,
                        },
                        "*",
                      );
                      console.log("GTM load message sent to parent window");
                    } catch (e) {
                      console.log(
                        "Failed to send message to parent, loading in current window",
                      );

                      // Fallback: load in current window
                      const GTM_ID = "GTM-PS96TMC8";
                      if (!document.querySelector(`script[src*="${GTM_ID}"]`)) {
                        window.dataLayer = window.dataLayer || [];
                        window.dataLayer.push({
                          "gtm.start": new Date().getTime(),
                          event: "gtm.js",
                        });

                        const script = document.createElement("script");
                        script.async = true;
                        script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
                        script.onload = () =>
                          console.log("GTM loaded in iframe");
                        document.head.appendChild(script);
                      }
                    }
                  }}
                >
                  {ACCEPT_MESSAGE[
                    botData?.aiBotConfig?.language?.toLowerCase()
                  ] || ACCEPT_MESSAGE.english}
                </button>
                <button
                  className={`cbb-decline-cookie-btn ${chatConfig?.theme === "dark" ? "dark-font" : "light-font"}`}
                  onClick={() => {
                    setConsent(false);

                    // Set declined state
                    window.localStorage.setItem("privacy_policy", "false");
                    const expires = new Date();
                    expires.setTime(
                      expires.getTime() + 365 * 24 * 60 * 60 * 1000,
                    );
                    document.cookie = `privacy_policy=false; expires=${expires.toUTCString()}; path=/`;

                    console.log("Privacy policy declined, GTM will not load");
                  }}
                >
                  {DECLINE_MESSAGE[
                    botData?.aiBotConfig?.language?.toLowerCase()
                  ] || DECLINE_MESSAGE.english}
                </button>
              </div>
            </div>
          )}
          <ProtectedBranding
            chatConfig={chatConfig}
            environmentData={environmentData}
            // BrandingLogoCBB={BrandingLogoCBB}
            BrandingLogoOmni={BrandingLogoOmni}
            fromOutgrow={fromOutgrow}
            contactData={localContactData || contactData || null}
            hasUserSentMessage={hasUserSentMessage}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      ref={wrapperRef}
      className={`cbb-full-screen-preview-wrapper full-screen-mobile ${responsive || isMobile ? "cbb-mobile-live" : ""}`}
      style={{
        fontFamily: chatConfig?.typography?.font?.fontFamily,
        overscrollBehavior: "contain",
        touchAction: "pan-y pinch-zoom",
        isolation: "isolate",
        contain: "layout style paint",
      }}
    >
      <div
        className={`cbb-full-preview-container-main preview-container-main in-page live-bot`}
        style={{
          background: chatConfig?.chatBgColor,
        }}
      >
        {environment === "dev" && (
          <div className={`cbb-preview-head preview-head`}>
            <h1
              className={`cbb-preview-title ${
                chatConfig?.theme === "dark" ? "dark-font" : "light-font"
              }`}
            >
              {botName}
            </h1>
            <div className="cbb-right-head-preview right-head-preview">
              <button
                className="cbb-close-preview-button close-preview-button"
                onClick={() => router(-1)}
              >
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 10 10"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5.91615 5.00779L9.80995 1.11387C10.0634 0.860582 10.0634 0.451058 9.80995 0.197775C9.55667 -0.0555083 9.14715 -0.0555083 8.89386 0.197775L4.99994 4.09169L1.10614 0.197775C0.85274 -0.0555083 0.443335 -0.0555083 0.190051 0.197775C-0.0633505 0.451058 -0.0633505 0.860582 0.190051 1.11387L4.08385 5.00779L0.190051 8.90171C-0.0633505 9.15499 -0.0633505 9.56451 0.190051 9.8178C0.316278 9.94414 0.482247 10.0076 0.648097 10.0076C0.813947 10.0076 0.979797 9.94414 1.10614 9.8178L4.99994 5.92388L8.89386 9.8178C9.02021 9.94414 9.18606 10.0076 9.35191 10.0076C9.51776 10.0076 9.68361 9.94414 9.80995 9.8178C10.0634 9.56451 10.0634 9.15499 9.80995 8.90171L5.91615 5.00779Z"
                    fill="#FB5F66"
                  />
                </svg>
                Close Preview
              </button>
            </div>
          </div>
        )}
        <div
          className={`cbb-full-preview-header embed-header no-radius`}
          style={{
            background: chatConfig?.greetingConfig?.headerFooterBgColor,
          }}
        >
          {chatConfig?.logo ? (
            <img
              className="cbb-full-preview-logo"
              src={chatConfig?.logo}
              alt="boticon"
            />
          ) : fromOutgrow ? (
            <OutgrowBotIcon />
          ) : (
            <BotIconOld />
          )}
          <div className="cbb-full-preview-header-content embed-header-desc grid gap-[5px]">
            <h3
              className={`cbb-full-preview-heading !font-medium`}
              style={{
                fontSize: chatConfig?.greetingConfig?.header?.fontSize + "px",
                color: getTextColor(
                  chatConfig?.greetingConfig?.headerFooterBgColor,
                ),
              }}
            >
              {chatConfig?.greetingConfig?.header?.text.slice(0, 30)}
            </h3>
            {chatConfig?.greetingConfig?.description?.enabled && (
              <p
                className={`cbb-full-preview-description`}
                style={{
                  fontSize:
                    chatConfig?.greetingConfig?.description?.fontSize + "px",
                  color: getTextColor(
                    chatConfig?.greetingConfig?.headerFooterBgColor,
                  ),
                }}
              >
                {fromOutgrow
                  ? " Enhance your business with Outgrow"
                  : chatConfig?.greetingConfig?.description?.text}
              </p>
            )}{" "}
          </div>
        </div>
        <div
          className={`cbb-full-preview-container previewContainer new-scrollbar embed fx-d-col relative ${responsive || isMobile ? "embed-scroll" : ""}`}
          ref={scrollToButtomRef}
          style={{
            background: chatConfig?.chatBgColor,
          }}
        >
          <div className="cbb-full-chats-wrapper centered-div">
            <div className="cbb-full-chats-container chatDataContainer">
              <>
                {message?.map((msg, currentIndex) => {
                  if (msg.name === "agent-start") {
                    return (
                      <div className="cbb-full-live-agent-wrapper live-agent-wrapper wd-100 flex items-center gap-[15px]">
                        <hr
                          className={`cbb-full-hr live-agent-hr ${
                            chatConfig?.theme === "dark"
                              ? "border-[#f8f8f8]"
                              : "border-[#44444421]"
                          } wd-100 border`}
                        />
                        <span
                          className={`cbb-full-live-agent-text live-agent-text ${
                            chatConfig?.theme === "dark"
                              ? "dark-font"
                              : "light-font"
                          } flex-cnt w-[500px] text-[11px] uppercase`}
                        >
                          Chat with live agent
                        </span>
                        <hr
                          className={`cbb-full-hr live-agent-hr ${
                            chatConfig?.theme === "dark"
                              ? "border-[#f8f8f8]"
                              : "border-[#44444421]"
                          } wd-100 border`}
                        />
                      </div>
                    );
                  } else if (msg.name === "agent-end") {
                    return (
                      <div className="cbb-full-live-agent-wrapper live-agent-wrapper wd-100 flex items-center gap-[15px]">
                        <hr
                          className={`cbb-full-hr live-agent-hr ${
                            chatConfig?.theme === "dark"
                              ? "border-[#f8f8f8]"
                              : "border-[#44444421]"
                          } wd-100 border`}
                        />
                        <span
                          className={`cbb-full-live-agent-text live-agent-text ${
                            chatConfig?.theme === "dark"
                              ? "dark-font"
                              : "light-font"
                          } flex-cnt w-[500px] text-[11px] uppercase`}
                        >
                          Chat end with live agent
                        </span>
                        <hr
                          className={`cbb-full-hr live-agent-hr ${
                            chatConfig?.theme === "dark"
                              ? "border-[#f8f8f8]"
                              : "border-[#44444421]"
                          } wd-100 border`}
                        />
                      </div>
                    );
                  } else if (msg.name === "button") {
                    return (
                      <div
                        key={currentIndex}
                        className="cbb-msg-container gid gap-[10px]"
                      >
                        {msg.author === 0 ? (
                          <BotComponent
                            reasoning={reasoning}
                            hideClickableOptions={
                              suggestedQuestionsData?.length > 0
                            }
                            ref={botComponentRef}
                            responseCacheOptions={responseCacheOptions}
                            toolLoader={
                              message.length - 1 === currentIndex
                                ? usingToolLoader
                                : false
                            }
                            currentIndex={currentIndex}
                            chatConfig={chatConfig}
                            msg={msg}
                            embed={embed}
                            botData={botData}
                            starterQues={starterQues}
                            activeCat={activeCat}
                            handleStarterQues={handleStarterQues}
                            textColor={textColor}
                            suggestedQuesAns={suggestedQuesAns}
                            message={message}
                            setReadPdf={setReadPdf}
                            setReadPdfText={setReadPdfText}
                            dataSource={dataSource}
                            handleReaction={handleReaction}
                            userCredential={userCredential}
                            calendlyConfig={calendlyConfig}
                            handleUserReply={handleUserReply}
                            isAnswerSaved={isAnswerSaved}
                            handleRegenerate={handleRegenerate}
                            responseLoader={responseLoader}
                            previousChat={props.resumeConversationData}
                            customGoalsMsg={customGoalsMsg.current}
                            steps={steps.current}
                            setDisableInput={setDisableInput}
                            isLast={islast}
                            isOverLimit={overLimit}
                          />
                        ) : (
                          <UserComponent
                            isCurrentLastUserMessage={
                              lastUserMsgIndex === currentIndex
                            }
                            currentIndex={currentIndex}
                            chatConfig={chatConfig}
                            msg={msg}
                            textColor={textColor}
                            embed={embed}
                            previousChat={props.resumeConversationData}
                          />
                        )}
                        {!msg.hasAnswered && (
                          <div className="cbb-button-container buttonContainer">
                            {msg.options
                              .sort(
                                (a, b) =>
                                  (a.image?.url.length || 0) -
                                  (b.image?.url.length || 0),
                              )
                              .map((value, index) => (
                                <div
                                  className="cbb-question-button questionButton flex-cnt fx-d-col gap-10"
                                  key={index}
                                  onClick={() => {
                                    handleButtonReply(
                                      currentIndex,
                                      value.name,
                                      index,
                                      value.value,
                                    );
                                  }}
                                >
                                  {msg?.showImg && value.image && (
                                    <img src={value.image.url} alt="" />
                                  )}
                                  <div className="cbb-option-title-wrapper option-title-wrapper flex-cnt fx-strt wd-100 gap-10">
                                    <div className="cbb-circle-radio-outer circle-radio-outer">
                                      <div className="cbb-circle-radio-inner circle-radio-inner"></div>
                                    </div>
                                    <p
                                      style={{
                                        fontSize:
                                          chatConfig?.typography?.size + "px",
                                      }}
                                    >
                                      {value.name}
                                    </p>
                                  </div>
                                </div>
                              ))}
                          </div>
                        )}
                      </div>
                    );
                  } else {
                    return (
                      <Fragment key={currentIndex}>
                        <div className="cbb-msg-inner-wrapper flex flex-col !items-stretch gap-[10px]">
                          {msg.author === 0 ? (
                            <BotComponent
                              reasoning={reasoning}
                              hideClickableOptions={
                                suggestedQuestionsData?.length > 0
                              }
                              ref={botComponentRef}
                              responseCacheOptions={responseCacheOptions}
                              toolLoader={
                                message.length - 1 === currentIndex
                                  ? usingToolLoader
                                  : false
                              }
                              botData={botData}
                              currentIndex={currentIndex}
                              chatConfig={chatConfig}
                              msg={msg}
                              starterQues={starterQues}
                              activeCat={activeCat}
                              handleStarterQues={handleStarterQues}
                              textColor={textColor}
                              suggestedQuesAns={suggestedQuesAns}
                              message={message}
                              setReadPdf={setReadPdf}
                              setReadPdfText={setReadPdfText}
                              dataSource={dataSource}
                              handleReaction={handleReaction}
                              userCredential={userCredential}
                              calendlyConfig={calendlyConfig}
                              handleUserReply={handleUserReply}
                              isAnswerSaved={isAnswerSaved}
                              handleRegenerate={handleRegenerate}
                              responseLoader={responseLoader}
                              previousChat={props.resumeConversationData}
                              customGoalsMsg={customGoalsMsg.current}
                              steps={steps.current}
                              setDisableInput={setDisableInput}
                              isLast={islast}
                              isOverLimit={overLimit}
                            />
                          ) : (
                            <UserComponent
                              isCurrentLastUserMessage={
                                lastUserMsgIndex === currentIndex
                              }
                              currentIndex={currentIndex}
                              chatConfig={chatConfig}
                              msg={msg}
                              textColor={textColor}
                              embed={embed}
                              botType={botType}
                              previousChat={props.resumeConversationData}
                            />
                          )}
                        </div>
                      </Fragment>
                    );
                  }
                })}
                <span ref={scrollRef}></span>
              </>
            </div>
          </div>
          {showGoToBottom && !responseLoader && (
            <div
              className={`cbb-got-to-bottom-button gotobottom-btn fx sticky bottom-[20px] left-1/2 h-[36px] w-[36px] -translate-x-1/2 cursor-pointer items-center justify-center rounded-[100px] ${
                chatConfig?.theme === "dark"
                  ? "bg-[#6D6D6D]"
                  : "bg-[#fff] drop-shadow-[0_4px_10px_rgba(0,0,0,0.15)]"
              }`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                scrollToBottom(e);
              }}
            >
              <GoToBottomIcon
                fill={chatConfig?.theme === "dark" ? "#fff" : "#444"}
              />
            </div>
          )}
        </div>
        <div className="cbb-chat-screen-footer footer-sec !bg-transparent">
          {isAiResError && !isCustomProviderError && (
            <div className="cbb-error-msg-wrapper mt-3 flex w-full items-center justify-center">
              <div
                className={`cbb-retry-error-msg retry-error-msg fx w-1/2 items-center justify-between gap-[20px] rounded-[15px] px-[16px] py-[10px]`}
                style={{ background: messageBoxBg }}
              >
                <div className="cbb-error-msg-content al-cnt grid grid-flow-col gap-[6px]">
                  <svg
                    width="17"
                    height="16"
                    viewBox="0 0 17 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className={`h-[16px] w-[16px]`}
                  >
                    <g clipPath="url(#clip0_3826_34009)">
                      <path
                        d="M8.60743 0C7.02518 0 5.47846 0.469192 4.16286 1.34824C2.84727 2.22729 1.82189 3.47672 1.21639 4.93853C0.610888 6.40034 0.452461 8.00887 0.761143 9.56072C1.06982 11.1126 1.83175 12.538 2.95057 13.6569C4.06939 14.7757 5.49486 15.5376 7.0467 15.8463C8.59855 16.155 10.2071 15.9965 11.6689 15.391C13.1307 14.7855 14.3801 13.7602 15.2592 12.4446C16.1382 11.129 16.6074 9.58225 16.6074 8C16.6051 5.87897 15.7615 3.84547 14.2617 2.34568C12.762 0.845886 10.7285 0.00229405 8.60743 0ZM8.60743 14.6667C7.28888 14.6667 5.99995 14.2757 4.90362 13.5431C3.8073 12.8106 2.95281 11.7694 2.44823 10.5512C1.94364 9.33305 1.81162 7.99261 2.06886 6.6994C2.32609 5.40619 2.96103 4.21831 3.89338 3.28596C4.82573 2.35361 6.01362 1.71867 7.30682 1.46143C8.60003 1.2042 9.94048 1.33622 11.1586 1.8408C12.3768 2.34539 13.418 3.19987 14.1506 4.2962C14.8831 5.39253 15.2741 6.68146 15.2741 8C15.2722 9.76752 14.5691 11.4621 13.3193 12.7119C12.0695 13.9617 10.3749 14.6647 8.60743 14.6667Z"
                        fill={getTextColor(messageBoxBg)}
                      />
                      <path
                        d="M8.60775 6.66797H7.94108C7.76427 6.66797 7.5947 6.73821 7.46968 6.86323C7.34465 6.98826 7.27441 7.15782 7.27441 7.33464C7.27441 7.51145 7.34465 7.68102 7.46968 7.80604C7.5947 7.93106 7.76427 8.0013 7.94108 8.0013H8.60775V12.0013C8.60775 12.1781 8.67799 12.3477 8.80301 12.4727C8.92803 12.5977 9.0976 12.668 9.27441 12.668C9.45123 12.668 9.62079 12.5977 9.74582 12.4727C9.87084 12.3477 9.94108 12.1781 9.94108 12.0013V8.0013C9.94108 7.64768 9.8006 7.30854 9.55056 7.05849C9.30051 6.80844 8.96137 6.66797 8.60775 6.66797Z"
                        fill={getTextColor(messageBoxBg)}
                      />
                      <path
                        d="M8.60742 5.33203C9.15971 5.33203 9.60742 4.88432 9.60742 4.33203C9.60742 3.77975 9.15971 3.33203 8.60742 3.33203C8.05514 3.33203 7.60742 3.77975 7.60742 4.33203C7.60742 4.88432 8.05514 5.33203 8.60742 5.33203Z"
                        fill={getTextColor(messageBoxBg)}
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_3826_34009">
                        <rect
                          width="16"
                          height="16"
                          fill="white"
                          transform="translate(0.607422)"
                        />
                      </clipPath>
                    </defs>
                  </svg>
                  <p
                    className={`cbb-error-text text-[12px]`}
                    style={{
                      color: getTextColor(messageBoxBg),
                    }}
                  >
                    {ERROR_CONNECTION_ISSUE[
                      botData?.aiBotConfig?.language?.toLowerCase()
                    ] || ERROR_CONNECTION_ISSUE.english}
                  </p>
                </div>
                <button
                  style={{
                    background: getTextColor(messageBoxBg),
                    color: messageBoxBg,
                  }}
                  className={`cbb-retry-button fx items-center gap-[6px] whitespace-nowrap rounded-[100px] px-[12px] py-[6px] text-[12px]`}
                  onClick={() => handleRegenerate(true)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="13"
                    height="12"
                    viewBox="0 0 13 12"
                    fill="none"
                    className={``}
                  >
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M6.60742 2.0005C5.31642 2.0005 3.99392 2.619 3.05842 3.75H4.10742C4.24003 3.75 4.36721 3.80268 4.46098 3.89645C4.55474 3.99021 4.60742 4.11739 4.60742 4.25C4.60742 4.38261 4.55474 4.50979 4.46098 4.60355C4.36721 4.69732 4.24003 4.75 4.10742 4.75H2.10742C1.97481 4.75 1.84764 4.69732 1.75387 4.60355C1.6601 4.50979 1.60742 4.38261 1.60742 4.25L1.60742 2C1.60742 1.86739 1.6601 1.74021 1.75387 1.64645C1.84764 1.55268 1.97481 1.5 2.10742 1.5C2.24003 1.5 2.36721 1.55268 2.46098 1.64645C2.55474 1.74021 2.60742 1.86739 2.60742 2L2.60742 2.756C3.69742 1.631 5.14842 1 6.60742 1C9.36842 1 11.6064 3.2385 11.6064 6C11.6064 8.761 9.36792 11 6.60742 11C5.68969 11.0002 4.78961 10.7478 4.00582 10.2704C3.22203 9.79299 2.58479 9.10904 2.16392 8.2935C2.13236 8.23507 2.11275 8.17094 2.10624 8.10485C2.09974 8.03876 2.10646 7.97204 2.12602 7.90858C2.14558 7.84511 2.17759 7.78619 2.22018 7.73523C2.26276 7.68427 2.31507 7.64231 2.37405 7.61179C2.43303 7.58127 2.4975 7.5628 2.5637 7.55747C2.62989 7.55214 2.69648 7.56005 2.75959 7.58074C2.82269 7.60142 2.88104 7.63447 2.93124 7.67796C2.98143 7.72144 3.02246 7.77449 3.05192 7.834C3.37746 8.46519 3.86517 8.99833 4.46495 9.37864C5.06473 9.75895 5.75495 9.97271 6.4647 9.99797C7.17444 10.0232 7.87811 9.85907 8.50341 9.52236C9.12871 9.18564 9.65309 8.68852 10.0227 8.08207C10.3922 7.47562 10.5937 6.7817 10.6063 6.07162C10.619 5.36154 10.4423 4.6609 10.0945 4.04169C9.74674 3.42248 9.24037 2.90703 8.62743 2.5483C8.0145 2.18957 7.31711 2.00049 6.60692 2.0005H6.60742Z"
                      fill={messageBoxBg}
                    />
                  </svg>
                  {RETRY_LABEL[botData?.aiBotConfig?.language?.toLowerCase()] ||
                    RETRY_LABEL.english}
                </button>
              </div>
            </div>
          )}

          {isCustomProviderError && (cooldownActive || !isAiResError) && (
            <div className="cbb-error-msg-wrapper mt-3 flex w-full items-center justify-center">
              <div
                className={`cbb-retry-error-msg retry-error-msg fx w-1/2 items-center justify-between gap-[20px] rounded-[15px] px-[16px] py-[10px]`}
                style={{ background: messageBoxBg }}
              >
                <div className="cbb-error-msg-content al-cnt grid grid-flow-col gap-[6px]">
                  <svg
                    width="17"
                    height="16"
                    viewBox="0 0 17 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className={`h-[16px] w-[16px]`}
                  >
                    <g clipPath="url(#clip0_3826_34009)">
                      <path
                        d="M8.60743 0C7.02518 0 5.47846 0.469192 4.16286 1.34824C2.84727 2.22729 1.82189 3.47672 1.21639 4.93853C0.610888 6.40034 0.452461 8.00887 0.761143 9.56072C1.06982 11.1126 1.83175 12.538 2.95057 13.6569C4.06939 14.7757 5.49486 15.5376 7.0467 15.8463C8.59855 16.155 10.2071 15.9965 11.6689 15.391C13.1307 14.7855 14.3801 13.7602 15.2592 12.4446C16.1382 11.129 16.6074 9.58225 16.6074 8C16.6051 5.87897 15.7615 3.84547 14.2617 2.34568C12.762 0.845886 10.7285 0.00229405 8.60743 0ZM8.60743 14.6667C7.28888 14.6667 5.99995 14.2757 4.90362 13.5431C3.8073 12.8106 2.95281 11.7694 2.44823 10.5512C1.94364 9.33305 1.81162 7.99261 2.06886 6.6994C2.32609 5.40619 2.96103 4.21831 3.89338 3.28596C4.82573 2.35361 6.01362 1.71867 7.30682 1.46143C8.60003 1.2042 9.94048 1.33622 11.1586 1.8408C12.3768 2.34539 13.418 3.19987 14.1506 4.2962C14.8831 5.39253 15.2741 6.68146 15.2741 8C15.2722 9.76752 14.5691 11.4621 13.3193 12.7119C12.0695 13.9617 10.3749 14.6647 8.60743 14.6667Z"
                        fill={getTextColor(messageBoxBg)}
                      />
                      <path
                        d="M8.60775 6.66797H7.94108C7.76427 6.66797 7.5947 6.73821 7.46968 6.86323C7.34465 6.98826 7.27441 7.15782 7.27441 7.33464C7.27441 7.51145 7.34465 7.68102 7.46968 7.80604C7.5947 7.93106 7.76427 8.0013 7.94108 8.0013H8.60775V12.0013C8.60775 12.1781 8.67799 12.3477 8.80301 12.4727C8.92803 12.5977 9.0976 12.668 9.27441 12.668C9.45123 12.668 9.62079 12.5977 9.74582 12.4727C9.87084 12.3477 9.94108 12.1781 9.94108 12.0013V8.0013C9.94108 7.64768 9.8006 7.30854 9.55056 7.05849C9.30051 6.80844 8.96137 6.66797 8.60775 6.66797Z"
                        fill={getTextColor(messageBoxBg)}
                      />
                      <path
                        d="M8.60742 5.33203C9.15971 5.33203 9.60742 4.88432 9.60742 4.33203C9.60742 3.77975 9.15971 3.33203 8.60742 3.33203C8.05514 3.33203 7.60742 3.77975 7.60742 4.33203C7.60742 4.88432 8.05514 5.33203 8.60742 5.33203Z"
                        fill={getTextColor(messageBoxBg)}
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_3826_34009">
                        <rect
                          width="16"
                          height="16"
                          fill="white"
                          transform="translate(0.607422)"
                        />
                      </clipPath>
                    </defs>
                  </svg>
                  <p
                    className={`cbb-error-text text-[12px]`}
                    style={{
                      color: getTextColor(messageBoxBg),
                    }}
                  >
                    {cooldownMessage ||
                      ERROR_CONNECTION_ISSUE[
                        botData?.aiBotConfig?.language?.toLowerCase()
                      ] ||
                      ERROR_CONNECTION_ISSUE.english}
                  </p>
                </div>
                <button
                  style={{
                    background: getTextColor(messageBoxBg),
                    color: messageBoxBg,
                  }}
                  className={`cbb-retry-button fx items-center gap-[6px] rounded-[100px] px-[12px] py-[6px] text-[12px]`}
                  onClick={() => {
                    handleRegenerate(true);
                    setIsCustomProviderError(false);
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="13"
                    height="12"
                    viewBox="0 0 13 12"
                    fill="none"
                    className={``}
                  >
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M6.60742 2.0005C5.31642 2.0005 3.99392 2.619 3.05842 3.75H4.10742C4.24003 3.75 4.36721 3.80268 4.46098 3.89645C4.55474 3.99021 4.60742 4.11739 4.60742 4.25C4.60742 4.38261 4.55474 4.50979 4.46098 4.60355C4.36721 4.69732 4.24003 4.75 4.10742 4.75H2.10742C1.97481 4.75 1.84764 4.69732 1.75387 4.60355C1.6601 4.50979 1.60742 4.38261 1.60742 4.25L1.60742 2C1.60742 1.86739 1.6601 1.74021 1.75387 1.64645C1.84764 1.55268 1.97481 1.5 2.10742 1.5C2.24003 1.5 2.36721 1.55268 2.46098 1.64645C2.55474 1.74021 2.60742 1.86739 2.60742 2L2.60742 2.756C3.69742 1.631 5.14842 1 6.60742 1C9.36842 1 11.6064 3.2385 11.6064 6C11.6064 8.761 9.36792 11 6.60742 11C5.68969 11.0002 4.78961 10.7478 4.00582 10.2704C3.22203 9.79299 2.58479 9.10904 2.16392 8.2935C2.13236 8.23507 2.11275 8.17094 2.10624 8.10485C2.09974 8.03876 2.10646 7.97204 2.12602 7.90858C2.14558 7.84511 2.17759 7.78619 2.22018 7.73523C2.26276 7.68427 2.31507 7.64231 2.37405 7.61179C2.43303 7.58127 2.4975 7.5628 2.5637 7.55747C2.62989 7.55214 2.69648 7.56005 2.75959 7.58074C2.82269 7.60142 2.88104 7.63447 2.93124 7.67796C2.98143 7.72144 3.02246 7.77449 3.05192 7.834C3.37746 8.46519 3.86517 8.99833 4.46495 9.37864C5.06473 9.75895 5.75495 9.97271 6.4647 9.99797C7.17444 10.0232 7.87811 9.85907 8.50341 9.52236C9.12871 9.18564 9.65309 8.68852 10.0227 8.08207C10.3922 7.47562 10.5937 6.7817 10.6063 6.07162C10.619 5.36154 10.4423 4.6609 10.0945 4.04169C9.74674 3.42248 9.24037 2.90703 8.62743 2.5483C8.0145 2.18957 7.31711 2.00049 6.60692 2.0005H6.60742Z"
                      fill={messageBoxBg}
                    />
                  </svg>
                  Retry
                </button>
              </div>
            </div>
          )}
          <div
            className={`cbb-chat-bottom wd-50 branding ${
              chatConfig?.theme === "dark" ? " " : " "
            }`}
          >
            {/* {botData?.enableGemini && (
              <VoiceCoach url={undefined} botCustomId={botData?.botCustomId} />
            )} */}
            {/* suggested que and ans */}
            {suggestedQuestionsData.length > 0 && (
              <div className="cbb-suggested-questions suggested-que relative mx-auto w-[50%]">
                {/* <h6
                 
                  className={`mb-[10px] text-[14px] font-medium ${chatConfig?.theme === "dark" ? `dark-font` : `text-[#444]`}`}
                >
                  Suggestions
                </h6> */}
                <Swiper
                  modules={[Navigation, Pagination, Autoplay]}
                  spaceBetween={10}
                  slidesPerView="auto"
                  navigation={false}
                  pagination={false}
                  loop={false}
                  speed={600}
                  className="cbb-suggested-questions-swiper suggested-questions-swiper"
                  style={{
                    paddingInlineStart: "10px",
                    paddingInlineEnd: "10px",
                    paddingTop: `10px`,
                  }}
                  onSlideChange={(swiper) => {
                    if (swiper.isEnd) {
                      swiper.autoplay.stop();
                    }
                  }}
                >
                  {suggestedQuestionsData.map((item, index) => (
                    <SwiperSlide
                      className="cbb-suggested-questions-slide"
                      key={index}
                      style={{
                        width: "auto",
                        height: "auto",
                      }}
                    >
                      <motion.div
                        initial={{ opacity: 0, x: 20, scale: 0.2 }}
                        animate={{
                          opacity: 1,
                          x: 0,
                          scale: 1,
                          transition: {
                            duration: 0.4,
                            ease: "easeOut",
                            delay: index * 0.1,
                          },
                        }}
                        whileHover={{
                          scale: 1.02,
                          y: -1,
                          transition: { duration: 0.2 },
                        }}
                        whileTap={{ scale: 0.98 }}
                        className="cbb-suggested-question cursor-pointer whitespace-nowrap text-nowrap rounded-[100px] border px-[15px] py-[10px]"
                        onClick={() => {
                          const lastIndex = message.length
                            ? message.length - 1
                            : 0;
                          handleUserReply(lastIndex, false, item, false);
                        }}
                        style={{
                          fontSize: chatConfig?.typography?.size + "px",
                          border: `1px solid ${getTextColor(chatConfig?.chatBgColor)}`,
                          color: getTextColor(chatConfig?.chatBgColor),
                        }}
                      >
                        {item}
                      </motion.div>
                    </SwiperSlide>
                  ))}
                </Swiper>
                <div
                  style={{
                    background: `linear-gradient(90deg, ${getColorWithAlpha(chatConfig?.chatBgColor, 0)} 0%, ${chatConfig?.chatBgColor} 66.82%)`,
                  }}
                  className={`cbb-fadeout-div fadeout-div !right-[0] !top-0 !h-full !w-[56px]`}
                ></div>
              </div>
            )}
            <div
              style={{
                opacity: disableInput || islast || overLimit ? "0.3" : "1",
                borderRadius: `${borderRadius}px`,
                background: messageBoxBg,
              }}
              className={`cbb-user-container-preview user-container-preview wd-50 embed in-page-test !items-center`}
            >
              <textarea
                ref={textareaRef}
                className={`cbb-user-textarea userInput textBox new-scrollbar me-[10px] !bg-transparent`}
                style={{
                  color: getTextColor(messageBoxBg),
                }}
                placeholder={
                  COMPOSE_MESSAGE_PLACEHOLDER[
                    botData?.aiBotConfig?.language?.toLowerCase()
                  ] || COMPOSE_MESSAGE_PLACEHOLDER.english + "..."
                }
                autoFocus={
                  !isMobile &&
                  window.location.hostname.includes("chatbotbuilder.net")
                }
                value={newReply}
                onChange={handleButtomInput}
                onKeyDown={handleSendViaEnter}
                readOnly={disableInput || islast || overLimit}
                // onTouchStart={() => {
                //   if (!(disableInput || islast || overLimit)) {
                //     textareaRef.current?.focus();
                //   }
                // }}
                // onFocus={(e) => {
                //   if (isMobile) {
                //     e.target.style.transform = "translateY(0)";
                //   }
                // }}
              />
              <div
                className={`cbb-suggested-cta suggested-cta flex-cnt mb-[7px] h-full ${
                  !botData?.aiStarterQues?.data.length ? "none" : ""
                } ${
                  message[message.length - 1]?.text?.isMandatory
                    ? "opacity-30"
                    : ""
                }`}
              >
                <div
                  className="cbb-suggested-cta-content"
                  onClick={() => {
                    if (message[message.length - 1]?.isLoader) return;
                    setSuggestedQuesAns(true);
                    setStarterQues(botData?.aiStarterQues?.data);
                    triggerNudge(botData.aiBotConfig?.nudgeConfig);
                  }}
                >
                  <SuggestedQuesAns />
                </div>
              </div>
              <div className="cbb-full-userBtn userBtn hhhh btn-text-area me-[5px] h-full">
                <div
                  className="cbb-send-button-wrapper svg-box"
                  style={{
                    background: `${
                      botData?.url === "omni001-290" ||
                      botData?.url === "tarun-4" ||
                      botData?.url === "tarun-25"
                        ? "black"
                        : chatConfig?.color === "#F1F1F1"
                          ? ""
                          : chatConfig?.color
                    }`,
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    responseLoader ? handleStop() : handleSendReplyButton();
                  }}
                >
                  <span
                    className="cbb-send-button"
                    style={{
                      color: `${
                        chatConfig?.color === "#F1F1F1" ? "#fff" : textColor
                      }`,
                    }}
                  >
                    {responseLoader ? (
                      <svg
                        className="cbb-stop-generation-svg"
                        width="15"
                        height="15"
                        viewBox="0 0 15 15"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect
                          x="0.607422"
                          y="0.5"
                          width="14"
                          height="14"
                          rx="4"
                          fill={chatConfig?.typography?.color}
                        />
                      </svg>
                    ) : (
                      <svg
                        className="cbb-send-svg"
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="17"
                        viewBox="0 0 16 17"
                        fill="none"
                      >
                        <path
                          d="M1.12457 0.888736L15.5045 8.07234C15.7365 8.18823 15.8318 8.47283 15.7175 8.70801C15.6719 8.80185 15.597 8.87778 15.5045 8.92402L1.12487 16.1075C0.892888 16.2234 0.612165 16.1267 0.497855 15.8915C0.447871 15.7886 0.436284 15.6709 0.465235 15.5601L1.88993 10.1057C1.93705 9.92536 2.08403 9.78969 2.26558 9.759L8.70978 8.66968C8.78873 8.65634 8.85401 8.6035 8.88538 8.53167L8.90224 8.47457C8.92046 8.36373 8.85976 8.25804 8.76172 8.21654L8.70978 8.20141L2.22919 7.10639C2.04757 7.0757 1.90054 6.93995 1.85347 6.75949L0.46491 1.436C0.398763 1.18253 0.547815 0.922697 0.797827 0.855636C0.907113 0.826322 1.02317 0.838079 1.12457 0.888736Z"
                          fill={
                            botData?.url === "omni001-290" ||
                            botData?.url === "tarun-4" ||
                            botData?.url === "tarun-25"
                              ? "white"
                              : chatConfig?.typography?.color
                          }
                        />
                      </svg>
                    )}
                  </span>
                </div>
              </div>
            </div>
            {!isReplyValid && showError && error !== "Field is required" && (
              <span className="cbb-small-error-txt small-txt error check-error no-m-tb mt-10">
                {error}
              </span>
            )}
          </div>
          {botData?.cookieConsent && consent && (
            <div
              className={`cbb-cookie-consent cookieConsent ${chatConfig?.omniBranding ? "!mb-[-10px]" : ""}`}
              style={{
                background: deriveShade(chatConfig?.chatBgColor),
              }}
            >
              <div
                dangerouslySetInnerHTML={{
                  __html: botData?.cookieConsentContent,
                }}
                className={`cbb-cookie-consent-inner cookieConsent-inner`}
                style={{
                  color: getTextColor(deriveShade(chatConfig?.chatBgColor)),
                }}
              />
              <div className={`cbb-cookie-btn-wrapper btn bg-transparent`}>
                <button
                  className={`cbb-accept-cookie-btn`}
                  onClick={() => {
                    console.log("=== ACCEPT BUTTON CLICKED ===");

                    // Hide consent popup first
                    setConsent(false);

                    window.localStorage.setItem("privacy_policy", "true");
                    console.log(
                      "localStorage set:",
                      window.localStorage.getItem("privacy_policy"),
                    );

                    const expires = new Date();
                    expires.setTime(
                      expires.getTime() + 365 * 24 * 60 * 60 * 1000,
                    );
                    document.cookie = `privacy_policy=true; expires=${expires.toUTCString()}; path=/`;

                    loadGTMNow();
                  }}
                >
                  {ACCEPT_MESSAGE[
                    botData?.aiBotConfig?.language?.toLowerCase()
                  ] || ACCEPT_MESSAGE.english}
                </button>
                <button
                  className={`cbb-decline-cookie-btn`}
                  onClick={() => {
                    setConsent(false);
                    deleteCookies("privacy_policy");
                    window.localStorage.removeItem("privacy_policy");
                    setCookie("privacy_policy", "false");
                  }}
                >
                  {DECLINE_MESSAGE[
                    botData?.aiBotConfig?.language?.toLowerCase()
                  ] || DECLINE_MESSAGE.english}
                </button>
              </div>
            </div>
          )}
        </div>
        <ProtectedBranding
          chatConfig={chatConfig}
          environmentData={environmentData}
          // BrandingLogoCBB={BrandingLogoCBB}
          BrandingLogoOmni={BrandingLogoOmni}
          fromOutgrow={fromOutgrow}
          contactData={localContactData || contactData || null}
          hasUserSentMessage={hasUserSentMessage}
        />
      </div>
    </div>
  );
});

export default Preview;

interface Row {
  key: string;
  value: any;
  testValue: string;
}

const BrandingLogoOmni = () => (
  <svg
    width={29}
    height={28}
    viewBox="0 0 29 28"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clipPath="url(#clip0_2085_10186)">
      <path
        d="M22.718 10.8422C23.9774 10.2899 25.4734 10.8631 25.7644 12.2072C26.2009 14.2231 26.1325 16.3294 25.5467 18.3341C24.7263 21.1414 22.9439 23.5701 20.512 25.1947C18.0802 26.8192 15.154 27.5357 12.2466 27.2186C9.33926 26.9015 6.63632 25.571 4.61181 23.4605C2.58729 21.3499 1.37036 18.594 1.17446 15.676C0.978559 12.758 1.81617 9.86416 3.54041 7.50188C5.26467 5.13965 7.7655 3.4598 10.6044 2.75699C12.6317 2.25504 14.7391 2.27424 16.7351 2.79422C18.0659 3.14088 18.5764 4.65938 17.9722 5.89478C17.368 7.13012 15.867 7.59383 14.502 7.42712C13.606 7.31773 12.6908 7.37075 11.8012 7.591C10.0979 8.01272 8.59739 9.02061 7.56286 10.438C6.52828 11.8553 6.02575 13.5916 6.14327 15.3424C6.26079 17.0932 6.99094 18.7468 8.20566 20.0131C9.42037 21.2794 11.0421 22.0777 12.7865 22.268C14.531 22.4583 16.2867 22.0283 17.7458 21.0536C19.2049 20.0789 20.2744 18.6216 20.7665 16.9373C21.0236 16.0577 21.1147 15.1455 21.0426 14.2457C20.9328 12.8749 21.4586 11.3944 22.718 10.8422Z"
        fill="#FB5F66"
      />
      <path
        d="M27.2799 6.87081C26.8448 7.21147 25.5525 6.72189 24.1552 6.07874C23.0413 6.50435 22.321 6.26301 23.8277 7.88542C24.9258 9.06768 21.7416 8.92601 19.9035 10.7641C17.5372 13.2177 18.5705 18.7253 15.6631 14.1324C14.5676 12.4018 16.1237 17.6853 15.3855 17.9002C14.4066 18.065 14.5265 16.8718 14.1757 14.0777C14.0084 12.7458 13.8104 13.9541 13.5726 15.4165C14.59 17.8493 14.2542 16.2574 13.5286 17.139L13.3172 16.8999C13.1407 17.8116 12.9453 18.494 12.7283 18.2771C12.2273 17.7759 11.721 15.1833 11.0408 14.3259L10.9492 14.2224C10.9801 14.2533 11.0106 14.2879 11.0408 14.3259L13.3172 16.8999C13.4072 16.4354 13.4922 15.9114 13.5726 15.4165C13.2627 14.6752 12.827 13.5602 12.2385 11.8877C11.6122 9.40722 15.8552 10.8004 17.5817 9.42265C19.3083 8.04494 19.9668 6.25264 19.6963 4.42994C19.7303 3.80576 22.0608 5.11479 24.1552 6.07874C24.5117 5.94249 24.9085 5.73797 25.2856 5.38205C25.5541 5.12852 25.7666 4.85554 25.9041 4.56289L23.5358 0.590332C25.914 2.17858 26.4033 3.50021 25.9041 4.56289L27.2799 6.87081Z"
        fill="#444444"
      />
      <path
        d="M26.8215 1.85207C25.0928 0.123352 22.29 0.123353 20.5613 1.85207C18.8326 3.58079 18.8326 6.38359 20.5613 8.11231C22.29 9.84103 25.0928 9.84103 26.8215 8.11231C28.5502 6.38359 28.5502 3.58079 26.8215 1.85207Z"
        fill="#444444"
      />
      <path
        d="M17.51 11.1642C15.4356 9.08974 12.0722 9.08974 9.99776 11.1642C7.9233 13.2387 7.9233 16.602 9.99776 18.6765C12.0722 20.7509 15.4356 20.7509 17.51 18.6765C19.5845 16.602 19.5845 13.2387 17.51 11.1642Z"
        fill="#444444"
      />
    </g>
    <defs>
      <clipPath id="clip0_2085_10186">
        <rect
          width={27.6862}
          height={27.6862}
          fill="white"
          transform="translate(0.701172)"
        />
      </clipPath>
    </defs>
  </svg>
);

export const BrandingLogoCBB = ({ color }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M11.2374 7.32417L11.2362 7.34163V7.35914C11.2362 7.89245 11.1281 8.22884 10.9809 8.44996C10.8369 8.6663 10.6214 8.82361 10.3049 8.93664C9.97956 9.05282 9.56787 9.11458 9.06559 9.14387C8.62435 9.1696 8.14337 9.16916 7.61923 9.16868C7.54762 9.16862 7.47522 9.16855 7.40199 9.16855C7.32877 9.16855 7.25636 9.16862 7.18476 9.16868C6.66062 9.16916 6.17964 9.1696 5.7384 9.14387C5.23612 9.11458 4.82442 9.05282 4.49909 8.93664C4.18261 8.82361 3.96708 8.6663 3.82306 8.44996C3.67585 8.22884 3.56776 7.89245 3.56776 7.35914C3.56776 5.2349 5.291 3.48182 7.40199 3.48182C9.55907 3.48182 11.3861 5.13159 11.2374 7.32417Z"
      stroke={color}
      strokeWidth="1.03395"
    />
    <path
      d="M2.75549 3.99891C-0.0153649 8.08562 2.67627 11.0943 4.85986 10.9699C4.91074 10.967 4.95452 11.0067 4.95452 11.0576V12.9632C4.95452 13.0406 5.04861 13.0787 5.10251 13.0232L7.04553 11.0213C7.08165 10.9841 7.13302 10.9652 7.18471 10.9695C9.88113 11.1931 12.0041 10.6999 12.7894 9.15648C13.7737 7.22207 12.8324 4.84835 11.8847 3.81439C9.9029 1.05719 4.95449 0.755614 2.75549 3.99891Z"
      stroke="url(#paint0_linear_2541_12675)"
      strokeWidth="1.20628"
    />
    <ellipse
      cx="5.81734"
      cy="6.6689"
      rx="0.540974"
      ry="0.540973"
      fill="#FB5F66"
    />
    <ellipse
      cx="7.44039"
      cy="6.6689"
      rx="0.540974"
      ry="0.540973"
      fill="#FB5F66"
    />
    <ellipse
      cx="9.06343"
      cy="6.6689"
      rx="0.540974"
      ry="0.540973"
      fill="#FB5F66"
    />
    <defs>
      <linearGradient
        id="paint0_linear_2541_12675"
        x1="3.35457"
        y1="1.91882"
        x2="7.26257"
        y2="13.1757"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#FF898E" />
        <stop offset="0.546875" stopColor="#EA0D17" />
        <stop offset="1" stopColor="#FB5F66" />
      </linearGradient>
    </defs>
  </svg>
);

const SuggestedQuesAns = () => (
  <svg
    className="cp"
    xmlns="http://www.w3.org/2000/svg"
    width={30}
    height={30}
    viewBox="0 0 30 30"
    fill="none"
  >
    <path
      d="M7.08984 15.8789C7.49637 15.8789 7.87699 15.7788 8.22117 15.6138C7.88461 15.2699 7.88514 14.7196 8.22621 14.3786C8.56729 14.0375 9.11754 14.037 9.46143 14.3735C9.62648 14.0293 9.72656 13.6487 9.72656 13.2422C9.72656 11.7882 8.54379 10.6055 7.08984 10.6055C5.6359 10.6055 4.45312 11.7882 4.45312 13.2422C4.45312 14.6961 5.6359 15.8789 7.08984 15.8789Z"
      fill="#7A7A7A"
    />
    <path
      d="M2.69531 22.9102H3.57422V23.7891C3.57422 24.274 3.18023 24.668 2.69531 24.668C2.20951 24.668 1.81641 25.0611 1.81641 25.5469C1.81641 26.0327 2.20951 26.4258 2.69531 26.4258H4.45312C6.57574 26.4258 8.35072 24.9134 8.75924 22.9102H11.4844C12.9383 22.9102 14.1211 21.7274 14.1211 20.2734V6.21094C14.1211 4.75699 12.9383 3.57422 11.4844 3.57422H2.69531C1.24137 3.57422 0 4.75699 0 6.21094V20.2734C0 21.7274 1.24137 22.9102 2.69531 22.9102ZM7.08984 8.84766C9.51287 8.84766 11.4844 10.8192 11.4844 13.2422C11.4844 14.1363 11.2129 14.967 10.7522 15.6618L11.2269 16.1364C11.5702 16.4797 11.5702 17.036 11.2269 17.3793C10.8836 17.7226 10.3274 17.7226 9.98408 17.3793L9.50941 16.9046C8.81461 17.3652 7.98398 17.6367 7.08984 17.6367C4.66682 17.6367 2.69531 15.6652 2.69531 13.2422C2.69531 10.8192 4.66682 8.84766 7.08984 8.84766ZM22.1351 13.2422H23.6852L22.9102 11.6921L22.1351 13.2422Z"
      fill="#7A7A7A"
    />
    <path
      d="M27.3047 3.57422H18.5156C17.0617 3.57422 15.8789 4.75699 15.8789 6.21094V20.2734C15.8789 21.7274 17.0617 22.9102 18.5156 22.9102H21.2408C21.6493 24.9134 23.4243 26.4258 25.5469 26.4258H27.3047C27.7905 26.4258 28.1836 26.0327 28.1836 25.5469C28.1836 25.0611 27.7905 24.668 27.3047 24.668C26.8198 24.668 26.4258 24.274 26.4258 23.7891V22.9102H27.3047C28.7586 22.9102 30 21.7274 30 20.2734V6.21094C30 4.75699 28.7586 3.57422 27.3047 3.57422ZM26.8189 17.544C26.6103 17.6481 26.369 17.6652 26.1479 17.5915C25.9268 17.5178 25.7439 17.3593 25.6396 17.1509L24.5641 15H21.2562L20.1808 17.1509C19.9637 17.5843 19.4341 17.7612 19.0015 17.544C18.5672 17.3269 18.3912 16.799 18.6084 16.3647L22.124 9.33346C22.4227 8.73779 23.3977 8.73779 23.6964 9.33346L27.2121 16.3647C27.4291 16.799 27.2532 17.3269 26.8189 17.544Z"
      fill="#7A7A7A"
    />
  </svg>
);

const GoToBottomIcon = ({ fill }) => (
  <svg
    className="cbb-go-to-bottom-icon"
    width={16}
    height={10}
    viewBox="0 0 16 10"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M7.99997 9.12047C7.71322 9.12047 7.4265 9.01098 7.20788 8.79246L0.328227 1.91274C-0.109409 1.4751 -0.109409 0.765553 0.328227 0.328094C0.765686 -0.109365 1.4751 -0.109365 1.91277 0.328094L7.99997 6.41565L14.0872 0.328307C14.5249 -0.109152 15.2342 -0.109152 15.6716 0.328307C16.1095 0.765765 16.1095 1.47532 15.6716 1.91295L8.79207 8.79268C8.57334 9.01123 8.28662 9.12047 7.99997 9.12047Z"
      fill={fill}
    />
  </svg>
);
